import {platform} from '../platform/transport';
import type {PlatformMailStatus} from '../../src/platform-mail';
import {clearChatDrafts} from '../platform/drafts';
import {UNSAVED_WORK_EVENT, type UnsavedWorkQuery} from '../platform/unsaved-work';
import {MerchantDisputeSchema,type MerchantDisputeResponseInput} from '../../src/merchant-dispute-types';
import type {ReservationReviewInput} from '../../src/reservation-review';
import {PublicPaymentMethodsSchema,type PublicPaymentMethods} from '../../src/public-payment-method-types';
import {AccountPromotionsSchema,PromotionRedemptionSchema,type AccountPromotions,type PromotionCreate} from '../../src/billing-promotion-types';
import {AdminCardReviewSchema,CardReviewResult,type AdminCardReview,type CardReviewFilter,type CardReviewInput} from '../../src/billing-card-review-types';
import {AdminRefundClosuresSchema,RefundClosureResult,type AdminRefundClosures,type RefundClosureFilter,type RefundClosureRequest} from '../../src/billing-refund-closure-types';
import {AdminUpgradeReviewSchema,UpgradeReviewResult,UpgradeResolutionResult,type AdminUpgradeReview,type UpgradeReviewFilter,type UpgradeReviewRequest} from '../../src/billing-upgrade-review-types';
import {AdminMembershipReviewSchema,MembershipClosureResult,type AdminMembershipReview,type MembershipReviewFilter,type MembershipReviewRequest} from '../../src/billing-membership-review-types';
import {MessageFeedSchema,MessageInboxSchema,type MessageFeed,type MessageInbox} from '../../src/account-message-types';
import {reloadChangedFrontend} from './frontend-version';
import {clearRegistrationDraft} from './registration-draft';
import {InvoicePage,type InvoicePageData} from '../../src/invoice-types';
import type {BillingMethodRequest} from '../../src/billing-method-types';
import {CryptoRefundSchema,AdminCryptoRefundsSchema,type CryptoRefund,type AdminCryptoRefunds,type CryptoRefundActionInput} from '../../src/billing-refund-types';
import {CardRefundSchema,AdminCardRefundsSchema,type CardRefund,type AdminCardRefunds,type CardRefundRequest,type CardRefundActionInput} from '../../src/billing-card-refund-types';
import {AdminPaymentReviewSchema,PaymentReviewResult,type AdminPaymentReview,type PaymentReviewInput,type PaymentReviewFilter} from '../../src/billing-payment-review-types';
import {walletNetworkReviewReceipt,type NetworkReviewApproval} from '../../src/commerce-crypto/network-review-contract';
import type {CommerceWorkspace,CommerceAdmin} from '../../src/commerce-types';
import {CommerceEarningsSchema,type CommerceEarnings,EARNINGS_PATH} from '../../src/commerce-earnings-types';
import {MerchantCardWorkspaceSchema,MerchantCardOptionsSchema,MerchantCardCreatedSchema,type MerchantCardWorkspace,type MerchantCardOptions,type MerchantCardOfferRequest} from '../../src/merchant-card-types';
import {MerchantRefundOverviewSchema,MerchantRefundSchema,type MerchantRefundOverview,type MerchantRefundRequest,type MerchantRefundActionInput} from '../../src/merchant-card-refund-types';
import {MerchantCryptoWorkspaceSchema,MerchantCryptoOptionsSchema,type MerchantCryptoWorkspace,type MerchantCryptoOptions,type MerchantCryptoOfferRequest} from '../../src/merchant-crypto-types';
import {currentLocation, navigate} from '../router';
import {BillingPaymentSchema,BillingPaymentOptionsSchema,BillingCreditQuoteSchema,type BillingPayment,type BillingPaymentOptions,type BillingCreditQuote,type BillingPurchaseRequest} from '../../src/billing-payment-types';
import type {MembershipAction} from '../../src/billing-membership';
import type {DomainAccount,DomainRegistration,DomainAdmin} from '../../src/domain-types';
import {mergeHistoryPage, mergeNativeMessages} from './hermes/history-pages';
import type {NativeCommunicationOutput, NativeConversationPage} from '../../src/native-hermes-types';
import type {NativeCommunicationView,NativeConversation,NativeWorkDetail} from './types';
import {useSyncExternalStore} from 'react';
import {PasswordCredentialsSchema} from '../../src/auth-policy';
import {EmailCodeStartSchema, EmailCodeFinishSchema} from '../../src/email-sign-in-policy';
import {PRODUCT_ENTRY_LABEL} from '../../src/product';
import {ChatAttachmentSchema, type ChatAttachment} from '../../src/chat-attachments';
import {NativeTokenReconnectSchema, NativeTokenSnapshotSchema} from '../../src/native-hermes-types';
import {AccountCreditBalanceSchema, type AccountCreditBalance} from '../../src/account-credit-types';
import {AccountVirtualMachineStatusSchema, type AccountVirtualMachineStatus} from '../../src/account-virtual-machine';
import {AdminVirtualMachinesSchema, type AdminVirtualMachines} from '../../src/admin-virtual-machines';
import {AccountDataResetStatusSchema, type AccountDataResetStatus} from '../../src/account-data-reset-types';
import {AccountPreparationSchema, type AccountPreparation} from '../../src/account-preparation';
import {EventStreamDecoder} from '../../src/event-stream';
import {clearWorkspaceContext} from './workspace-location';
import {GOOGLE_ERROR_FRAGMENT, GoogleCallbackErrorSchema, GoogleStatusSchema, type GoogleService} from '../../src/google-types';
import {DiscordStatusSchema, type DiscordStatus} from '../../src/discord-types';
import type {AdminAccess, AdminOverview, AdminAccountsPage, AdminAccountDetail, AdminCreditPage,
  AdminUsage, AdminCatalog, AdminHealth, AdminAuditPage, AdminAccountAccessRequest, AdminAccountRoleRequest,
  AdminCreditGrantRequest, AdminGrantResult} from '../../src/admin-types';
import {BillingPlansSchema, AccountBillingSchema, BillingAdminOverviewSchema, BillingRedirectSchema,
  type BillingPlans, type AccountBilling, type BillingAdminOverview, type BillingPlanDraft, type BillingPlan} from '../../src/billing-types';
import type {AccountDocument, AccountSettings, AccountProfile, WhatsAppStatus, AppConfig, ErrorDetails, Health, Resource,
  AccountApplications, ApplicationRegistration, ApplicationStatus, ApplicationLogs, GoogleStatus, AuthMode,
  Session, HermesState, NativeRuntimeStatus, NativeSessionMapping, NativeSessionDetail, NativeAgentDetail,
  NativeEventBatch, NativeMessageResult, NativeTerminalTicket, NativeTokenSnapshot} from './types';

const idle = <T>(): Resource<T> => ({status: 'idle', data: null, error: null});
const ready = <T>(data: T): Resource<T> => ({status: 'ready', data, error: null});
const paymentResources=()=>({accountMessages:idle<MessageFeed>(),accountInvoices:idle<InvoicePageData>(),billingPayments:idle<Record<string,BillingPayment>>(),billingPaymentOptions:idle<BillingPaymentOptions>(),billingCreditQuote:idle<BillingCreditQuote>(),billingRefunds:idle<CryptoRefund[]>(),billingCardRefunds:idle<CardRefund[]>()});
const nativeResources = () => ({nativeHistory: idle<null>(), nativeDetail: idle<NativeWorkDetail>(), nativeAgent: idle<NativeAgentDetail>(),
  nativeWorkDetailsOpen: false,
  nativeConversation:idle<NativeConversation>(),
  nativeEvents: idle<NativeEventBatch>(), nativeMessageResult: idle<NativeMessageResult>(), nativeTokens: idle<NativeTokenSnapshot>(),
  nativeCommunication: idle<NativeCommunicationView>()});
const merchantResources=()=>({merchantCard:idle<MerchantCardWorkspace>(),merchantCardOptions:idle<MerchantCardOptions>(),merchantCrypto:idle<MerchantCryptoWorkspace>(),merchantCryptoOptions:idle<MerchantCryptoOptions>()});
const adminResources = () => ({adminPromotions:idle<AccountPromotions>(),adminInvoices:idle<InvoicePageData>(),adminAccess: idle<AdminAccess>(), adminOverview: idle<AdminOverview>(),
  adminMailSender:idle<PlatformMailStatus>(),
  adminMessages:idle<MessageFeed>(),adminMessageInbox:idle<MessageInbox>(),
  adminCryptoRefunds:idle<AdminCryptoRefunds>(),
  adminCardRefunds:idle<AdminCardRefunds>(),
  adminMerchantRefunds:idle<MerchantRefundOverview>(),
  adminPaymentReview:idle<AdminPaymentReview>(),
  adminCardReview:idle<AdminCardReview>(),
  adminUpgradeReview:idle<AdminUpgradeReview>(),
  adminMembershipReview:idle<AdminMembershipReview>(),
  adminRefundClosures:idle<AdminRefundClosures>(),
  adminVirtualMachines: idle<AdminVirtualMachines>(),
  adminAccounts: idle<AdminAccountsPage>(), adminAccount: idle<AdminAccountDetail>(), adminCredits: idle<AdminCreditPage>(),
  adminUsage: idle<AdminUsage>(), adminCatalog: idle<AdminCatalog>(), adminHealth: idle<AdminHealth>(),
  adminAudit: idle<AdminAuditPage>(), adminCommerce: idle<CommerceAdmin>(), adminDomains: idle<DomainAdmin>(), adminBilling: idle<BillingAdminOverview>()});
let state: HermesState = {config: idle<AppConfig>(), health: idle<Health>(), session: idle<Session | null>(),
  publicPaymentMethods:idle<PublicPaymentMethods>(),
  accountPreparation: idle<AccountPreparation>(),
  accountVirtualMachine: idle<AccountVirtualMachineStatus>(),
  accountDataReset: idle<AccountDataResetStatus>(),
  accountSettings: idle<AccountSettings>(), accountProfile: idle<AccountProfile>(), accountBalance: idle<AccountCreditBalance>(), whatsapp: idle<WhatsAppStatus>(), google: idle<GoogleStatus>(), discord: idle<DiscordStatus>(),
  accountApplications: idle<AccountApplications>(), applicationLogs: idle<ApplicationLogs>(), applicationLogSlug: null,
  ...adminResources(), ...paymentResources(), ...merchantResources(), commerce: idle<CommerceWorkspace>(), commerceEarnings: idle<CommerceEarnings>(), domains: idle<DomainAccount>(), billingPlans: idle<BillingPlans>(), accountBilling: idle<AccountBilling>(),
  nativeRuntime: idle<NativeRuntimeStatus>(), nativeSessions: idle<NativeSessionMapping[]>(), ...nativeResources(),
  nativeTarget: null, nativeSelectedAgentId: null, nativeConnection: {status: 'closed', error: null},
  walletAddress: null, operation: {label: null, error: null, scope: null}};
const listeners = new Set<() => void>();
const publish = (patch: Partial<HermesState>): void => {state = {...state, ...patch}; for (const listener of listeners) listener();};
let authGeneration = 0;
let sessionLoadGeneration = 0;
let sessionRead: {accountGeneration: number; generation: number; promise: Promise<void>} | null = null;
let accountSettingsGeneration = 0;
let accountPreparationGeneration = 0;
let accountSettingsRead: {accountGeneration: number; generation: number; promise: Promise<AccountSettings>} | null = null;
let accountVirtualMachineGeneration = 0;
let accountDataResetGeneration = 0;
let accountDataResetRequestId: string | null = null;
let accountDataResetObservedId: string | null = null;
let accountDataResetFinishedId: string | null = null;
let accountProfileGeneration = 0;
let accountBalanceGeneration = 0;
let accountRefresh: {accountGeneration: number; promise: Promise<void>} | null = null;
let workspaceWasHidden = false;
let workspaceResumedAt = 0;
let accountApplicationsGeneration = 0;
let applicationLogsGeneration = 0;
let whatsappGeneration = 0;
let googleGeneration = 0;
let discordGeneration = 0;
let nativeGeneration = 0;
// Only this mounted interface's submissions can request a later chat selection.
// Saved command effects remain on the native receipt; this is transient origin state.
const nativeInputOrigins = new Map<string, {accountId: string; agentId: string | null; location: string}>();
let nativeRuntimeGeneration = 0;
let nativeSessionsGeneration = 0;
let nativeDetailGeneration = 0;
let nativeDetailRead: {generation: number; sessionId: string; agentId: string | null; includeAgentActivity: boolean;
  promise: Promise<NativeWorkDetail | null>} | null = null;
let nativeAgentGeneration = 0;
let nativeOpening: {accountGeneration: number; generation: number; sessionId: string | null;
  promise: Promise<NativeSessionMapping | null>} | null = null;
let nativePollController: AbortController | null = null;
let nativeCommunicationController: AbortController | null = null;
let nativeTokenController: AbortController | null = null;
let nativeTokenGeneration = 0;
let initialLoad: Promise<void> | null = null;
let managementGeneration = 0;
const managementVersions = new Map<string, number>();
const managementLocations = new Map<string, string>();
let stopWalletWatch: (() => void) | null = null;

export class ApiError extends Error {
  constructor(public readonly details: ErrorDetails) {super(details.message); this.name = details.name;}
}
class TokenTransportError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : {cause});
    this.name = 'TokenTransportError';
  }
}
function diagnosticValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'bigint') return value.toString();
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular error reference]';
  seen.add(value);
  if (Array.isArray(value)) return value.map(item => diagnosticValue(item, seen));
  const entries = value instanceof Error ? {...value, name: value.name, message: value.message,
    stack: value.stack, cause: value.cause} : value;
  return Object.fromEntries(Object.entries(entries).map(([key, item]) => [key, diagnosticValue(item, seen)]));
}
export function errorDetails(error: unknown): ErrorDetails {
  if (error instanceof ApiError) return error.details;
  if (typeof error === 'string') {
    const nativeResponse = /^HTTP (\d{3}): ([\s\S]*)$/.exec(error);
    if (nativeResponse) {
      try {return apiResponseError(JSON.parse(nativeResponse[2]!), Number(nativeResponse[1]), nativeResponse[2]!);}
      catch (cause) {return {name: 'InvalidApiResponse', message: error, status: Number(nativeResponse[1]), cause: diagnosticValue(cause)};}
    }
  }
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    const details = diagnosticValue(error) as ErrorDetails;
    return {...details, name: typeof details.name === 'string' ? details.name : 'Error', message: error.message};
  }
  if (typeof error === 'object' && error !== null) return {name: 'Error',
    message: 'An error was returned without a message.', cause: diagnosticValue(error)};
  return {name: 'Error', message: String(error)};
}
export function formatError(error: ErrorDetails | Error | null | undefined): string {
  if (!error) return '';
  const details = error instanceof Error ? errorDetails(error) : error;
  const cause = details.cause === undefined ? '' : `\nCause: ${typeof details.cause === 'string' ? details.cause : JSON.stringify(details.cause, null, 2)}`;
  return `${details.stack ?? `${details.name}: ${details.message}`}${cause}`;
}

function base(): string {
  const configured = state.config.data;
  const baseUrl = import.meta.env.BASE_URL;
  if (!baseUrl.startsWith('/') || !baseUrl.endsWith('/')) throw new Error('The build must declare an absolute application base path.');
  return configured ? `${configured.basePath}/api` : `${baseUrl}api`;
}
function apiResponseError(body: unknown, status: number, text: string): ErrorDetails {
  const details = typeof body === 'object' && body !== null && 'error' in body ? body.error : null;
  if (details && typeof details === 'object' && 'message' in details && typeof details.message === 'string') {
    return {...details as ErrorDetails, name: 'name' in details && typeof details.name === 'string' ? details.name : 'HttpError', status};
  }
  return {name: 'HttpError', message: `HTTP ${status}: ${text}`, status};
}
async function responseBody<T>(response: Response, permitHealthError = false): Promise<T> {
  const text = await response.text();
  if (response.status === 204) return undefined as T;
  let body: unknown;
  try {body = JSON.parse(text);}
  catch (cause) {throw new ApiError({name: 'InvalidApiResponse', message: `HTTP ${response.status}: ${text}`, status: response.status,
    cause: errorDetails(cause)});}
  if (!response.ok && !permitHealthError) {
    throw new ApiError(apiResponseError(body, response.status, text));
  }
  return body as T;
}
function isAccountRequest(path: string): boolean {
  return path === '/auth/logout' || path.startsWith('/account/') || path.startsWith('/hermes/') || path.startsWith('/admin/');
}
async function waitForSessionRead(path: string, generation: number): Promise<void> {
  if (!isAccountRequest(path)) return;
  const pending = sessionRead;
  if (pending?.accountGeneration === generation && pending.generation === sessionLoadGeneration) await pending.promise;
  if (generation !== authGeneration) throw new ApiError({name: 'AccountChanged',
    message: 'The signed-in account changed while this request was being processed.'});
}
function rejectExpiredAccount(path: string, error: unknown, authenticated: Session | null, generation: number): void {
  if (isAccountRequest(path) && error instanceof ApiError && error.details.status === 401 && authenticated !== null &&
      generation === authGeneration && state.session.data?.csrfToken === authenticated.csrfToken) {
    if (error.details.code === 'desktop_account_page_session_ended') {
      clearAccount();
      publishSession({status: 'error', data: null, error: error.details});
    } else acceptSession(null);
    // Account failures do not belong to a sign-in submission.
    publish({operation: {...state.operation, error: error.details, scope: 'account'}});
  }
}
export async function request<T>(path: string, options: {body?: unknown; upload?: {file: File; metadata: string}; csrf?: string; signal?: AbortSignal; health?: boolean; method?: 'GET' | 'POST' | 'PUT' | 'DELETE'} = {}): Promise<T> {
  const authenticated = state.session.data;
  const generation = authGeneration;
  await waitForSessionRead(path, generation);
  const headers: Record<string, string> = {'Accept': 'application/json'};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.upload) {
    if (options.body !== undefined) throw new Error('An upload cannot also contain a JSON body.');
    headers['Content-Type'] = 'application/octet-stream';
    headers['x-eightbots-attachment'] = options.upload.metadata;
  }
  if (options.csrf) headers['x-csrf-token'] = options.csrf;
  const method = options.method ?? (options.body === undefined && !options.upload ? 'GET' : 'POST');
  const url = `${base()}${path}`;
  const signal = options.signal ?? (method === 'GET' ? AbortSignal.timeout(30_000) : undefined);
  const abortedRequest = (error: unknown): unknown => signal?.aborted && signal.reason?.name === 'TimeoutError'
    ? new ApiError({name: 'RequestTimeout', message: `${method} ${url} timed out. Reconnect to try this request again.`,
      cause: errorDetails(signal.reason)}) : signal?.aborted ? signal.reason : error;
  for (let attempt = 0; ; attempt++) {
    try {
      let response: Response;
      try {
        response = await platform().fetch(url, {method, credentials: 'same-origin',
          headers, ...(options.upload ? {body: options.upload.file} : options.body === undefined ? {} : {body: JSON.stringify(options.body)}),
          ...(signal ? {signal} : {})});
        const result = await responseBody<T>(response, options.health === true && response.status === 503);
        await waitForSessionRead(path, generation);
        return result;
      } catch (error) {
        if (signal?.aborted) throw abortedRequest(error);
        // A resumed browser can abort either fetch() or the response body. Only
        // transport failures are retried, never HTTP/auth/protocol errors or writes.
        if (error instanceof ApiError) throw error;
        const details = errorDetails(error);
        throw new ApiError({name: 'NetworkError', message: `${method} ${url}: ${details.message}`,
          ...(details.stack ? {stack: details.stack} : {}), cause: details});
      }
    } catch (error) {
      if (signal?.aborted) throw abortedRequest(error);
      if (method === 'GET' && signal && generation === authGeneration && error instanceof ApiError &&
          error.details.name === 'NetworkError' && attempt < 3) {
        await waitForTokenReconnect(signal);
        await abortableDelay(500 * 2 ** attempt, signal);
        if (signal.aborted) throw abortedRequest(error);
        if (generation !== authGeneration) throw error;
        continue;
      }
      await waitForSessionRead(path, generation);
      rejectExpiredAccount(path, error, authenticated, generation);
      if (path.startsWith('/admin/') && error instanceof ApiError && error.details.status === 403 && generation === authGeneration) {
        if (path === '/admin/access') {
          managementGeneration++;
          managementLocations.clear();
          publish({...adminResources(), adminAccess: {status: 'error', data: null, error: error.details}});
          void refreshSession().catch(() => undefined);
        } else {
          // An owner-only operation can be forbidden while this account still has
          // normal administrator access. Recheck that access before clearing it.
          void loadAdminAccess().catch(() => undefined);
        }
      }
      throw error;
    }
  }
}
type ResourceKey = {[K in keyof HermesState]: HermesState[K] extends Resource<unknown> ? K : never}[keyof HermesState];
type ResourceData<K extends ResourceKey> = HermesState[K] extends Resource<infer T> ? T : never;
async function query<K extends ResourceKey>(key: K, fetcher: () => Promise<ResourceData<K>>, valid = () => true,
  retainDataOnError = false): Promise<ResourceData<K>> {
  publish({[key]: {status: 'loading', data: state[key].data, error: null}});
  try {
    const data = await fetcher();
    if (valid()) publish({[key]: ready(data)});
    return data;
  } catch (error) {
    // Retained transcripts/settings are read-only evidence while reconnecting.
    // A rejected account/resource (including reset fences) must still be cleared.
    const rejectedResource = error instanceof ApiError && (error.details.status ?? 0) >= 400 && (error.details.status ?? 0) < 500;
    if (valid()) publish({[key]: {status: 'error', data: retainDataOnError && !rejectedResource ? state[key].data : null, error: errorDetails(error)}});
    throw error;
  }
}
type ManagementKey = keyof ReturnType<typeof adminResources> | keyof ReturnType<typeof merchantResources> | 'accountMessages' | 'billingPlans' | 'accountBilling' | 'accountInvoices' | 'billingPayments' | 'billingPaymentOptions' | 'billingRefunds' | 'billingCardRefunds' | 'commerce' | 'commerceEarnings' | 'domains';
function managementQueryPath(path: string, search = ''): string {
  const query = new URLSearchParams(search.replace(/^\?/, '')).toString();
  return query ? `${path}?${query}` : path;
}
async function readManagement<K extends ManagementKey>(key: K, path: string,
  parse?: (value: unknown) => ResourceData<K>): Promise<ResourceData<K>> {
  if (key !== 'billingPlans') session();
  const accountGeneration = authGeneration, generation = managementGeneration;
  const version = (managementVersions.get(key) ?? 0) + 1;
  managementVersions.set(key, version);
  if (managementLocations.get(key) !== path) publish({[key]: idle<ResourceData<K>>()});
  managementLocations.set(key, path);
  return query(key, async () => {
    const value = await request<ResourceData<K>>(path);
    return parse ? parse(value) : value;
  }, () => managementVersions.get(key) === version &&
    accountGeneration === authGeneration && generation === managementGeneration);
}
const loadAdminAccess = () => readManagement('adminAccess', '/admin/access');
const loadAdminOverview = () => readManagement('adminOverview', '/admin/overview');
const loadAdminVirtualMachines = () => readManagement('adminVirtualMachines', '/admin/virtual-machines', AdminVirtualMachinesSchema.parse);
const loadAdminAccounts = (search = '') => readManagement('adminAccounts', managementQueryPath('/admin/accounts', search));
const loadAdminAccount = (accountId: string) => readManagement('adminAccount', `/admin/accounts/${encodeURIComponent(accountId)}`);
const loadAdminCredits = (accountId: string, search = '') => readManagement('adminCredits',
  managementQueryPath(`/admin/accounts/${encodeURIComponent(accountId)}/credit-entries`, search));
const loadAdminUsage = (search = '') => readManagement('adminUsage', managementQueryPath('/admin/usage', search));
const loadAdminCatalog = () => readManagement('adminCatalog', '/admin/catalog');
const loadAdminMailSender = () => readManagement('adminMailSender', '/admin/mail-sender');
const loadAdminHealth = () => readManagement('adminHealth', '/admin/health');
const loadAdminAudit = (search = '') => readManagement('adminAudit', managementQueryPath('/admin/audit', search));
const loadBillingPlans = async () => {
  if(state.session.status!=='ready')await refreshSession();
  else if(sessionRead?.accountGeneration===authGeneration)await sessionRead.promise;
  return readManagement('billingPlans',state.session.data?'/account/billing/plans':'/billing/plans',BillingPlansSchema.parse);
};
let publicMethodsRequest:Promise<PublicPaymentMethods>|null=null;
const loadPublicPaymentMethods=()=>{
  if(publicMethodsRequest)return publicMethodsRequest;
  publicMethodsRequest=query('publicPaymentMethods',async()=>PublicPaymentMethodsSchema.parse(await request('/billing/payment-methods')))
    .finally(()=>{publicMethodsRequest=null;});
  return publicMethodsRequest;
};
const loadInvoices=(admin:boolean,search='')=>readManagement(admin?'adminInvoices':'accountInvoices',managementQueryPath(admin?'/admin/invoices':'/account/invoices',search),InvoicePage.parse);
const loadAccountBilling = () => readManagement('accountBilling', '/account/billing', AccountBillingSchema.parse);
const loadMessages=(accountId:string|null=null,search='')=>readManagement(accountId?'adminMessages':'accountMessages',
  managementQueryPath(accountId?`/admin/messages/${encodeURIComponent(accountId)}`:'/account/messages',search),MessageFeedSchema.parse);
const loadMessageInbox=(search='')=>readManagement('adminMessageInbox',managementQueryPath('/admin/messages',search),MessageInboxSchema.parse);
const loadBillingPaymentOptions=()=>readManagement('billingPaymentOptions','/account/billing/payment-options',BillingPaymentOptionsSchema.parse);
const loadBillingPayments=()=>{paymentReadVersion++;return readManagement('billingPayments','/account/billing/payments',value=>Object.fromEntries(BillingPaymentSchema.array().parse(value).map(payment=>[payment.id,payment])));};
const loadBillingRefunds=()=>readManagement('billingRefunds','/account/billing/crypto-refunds',CryptoRefundSchema.array().parse);
const loadAdminCryptoRefunds=()=>readManagement('adminCryptoRefunds','/admin/billing/crypto-refunds',AdminCryptoRefundsSchema.parse);
const loadBillingCardRefunds=()=>readManagement('billingCardRefunds','/account/billing/card-refunds',CardRefundSchema.array().parse);
const loadAdminCardRefunds=()=>readManagement('adminCardRefunds','/admin/billing/card-refunds',AdminCardRefundsSchema.parse);
const loadAdminRefundClosures=(filter:RefundClosureFilter={})=>{
  const query=new URLSearchParams();
  if(filter.orderId)query.set('orderId',filter.orderId);
  if(filter.beforeDate&&filter.beforeId){query.set('beforeDate',filter.beforeDate);query.set('beforeId',filter.beforeId);}
  return readManagement('adminRefundClosures',`/admin/billing/refund-closures?${query}`,AdminRefundClosuresSchema.parse);
};
const loadAdminCardReview=(filter:CardReviewFilter={})=>{
  const query=new URLSearchParams();
  for(const [key,value] of Object.entries(filter))if(value)query.set(key,value);
  return readManagement('adminCardReview',`/admin/billing/card-review?${query}`,AdminCardReviewSchema.parse);
};
const loadAdminUpgradeReview=(filter:UpgradeReviewFilter={})=>{
  const query=new URLSearchParams();
  for(const [key,value] of Object.entries(filter))if(value)query.set(key,value);
  return readManagement('adminUpgradeReview',`/admin/billing/upgrade-review?${query}`,AdminUpgradeReviewSchema.parse);
};
const loadAdminMembershipReview=(filter:MembershipReviewFilter={})=>{
  const query=new URLSearchParams();
  for(const [key,value] of Object.entries(filter))if(value)query.set(key,value);
  return readManagement('adminMembershipReview',`/admin/billing/membership-review?${query}`,AdminMembershipReviewSchema.parse);
};
const loadAdminPaymentReview=(filter:PaymentReviewFilter={})=>{
  const query=new URLSearchParams();
  if(filter.orderId)query.set('orderId',filter.orderId);
  if(filter.before){query.set('beforeDate',filter.before.createdAt);query.set('beforeId',filter.before.id);}
  return readManagement('adminPaymentReview',`/admin/billing/payment-review?${query}`,AdminPaymentReviewSchema.parse);
};
let paymentReadVersion=0,creditQuoteVersion=0;
async function loadBillingPayment(id:string){
  session();const generation=authGeneration,version=++paymentReadVersion;
  managementVersions.set('billingPayments',(managementVersions.get('billingPayments')??0)+1);
  try{
    const payment=BillingPaymentSchema.parse(await request(`/account/billing/payments/${encodeURIComponent(id)}`));
    if(generation===authGeneration&&version===paymentReadVersion)publish({billingPayments:ready({...state.billingPayments.data,[id]:payment})});
    return payment;
  }catch(error){if(generation===authGeneration&&version===paymentReadVersion)publish({billingPayments:{status:'error',data:state.billingPayments.data,error:errorDetails(error)}});throw error;}
}
async function quoteBillingCredits(amountUsd:string){
  const generation=authGeneration,version=++creditQuoteVersion;
  return query('billingCreditQuote',async()=>BillingCreditQuoteSchema.parse(await request('/account/billing/credit-quote',{body:{amountUsd},csrf:session().csrfToken})),()=>generation===authGeneration&&version===creditQuoteVersion);
}
async function billingPaymentMutation(path:string,body:unknown){
  const generation=authGeneration;
  const payment=BillingPaymentSchema.parse(await request(path,{body,csrf:session().csrfToken}));
  if(generation!==authGeneration)throw new Error('The account changed during checkout.');
  paymentReadVersion++;managementVersions.set('billingPayments',(managementVersions.get('billingPayments')??0)+1);
  publish({billingPayments:ready({...state.billingPayments.data,[payment.id]:payment})});return payment;
}
const loadAdminBilling = () => readManagement('adminBilling', '/admin/billing', BillingAdminOverviewSchema.parse);
const loadAdminPromotions=(accountId:string)=>readManagement('adminPromotions',`/admin/accounts/${encodeURIComponent(accountId)}/promotions`,AccountPromotionsSchema.parse);
async function promotionMutation(accountId:string,body:unknown,id?:string){
  const generation=authGeneration;
  const value=AccountPromotionsSchema.parse(await request(`/admin/accounts/${encodeURIComponent(accountId)}/promotions${id?`/${encodeURIComponent(id)}/revoke`:''}`,{body,csrf:session().csrfToken}));
  if(generation!==authGeneration)throw new Error('The account changed while the code was saved.');
  managementVersions.set('adminPromotions',(managementVersions.get('adminPromotions')??0)+1);
  publish({adminPromotions:ready(value)});return value;
}

async function adminMutation<T>(path: string, method: 'POST' | 'PUT' | 'DELETE', body: unknown,
  accountId?: string): Promise<T> {
  const generation = authGeneration;
  const result = await request<T>(path, {method, body, csrf: session().csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed while the administrative action was processed.');
  if (accountId === state.session.data?.accountId && (path.endsWith('/role') || path.endsWith('/revoke-sessions'))) {
    managementGeneration++;
    managementLocations.clear();
    publish(adminResources());
    void refreshSession().then(() => {
      if (state.session.data?.adminRole) return loadAdminAccess();
      return undefined;
    }).catch(() => undefined);
    return result;
  }
  // The write has committed. A later read failure must not invite a second write.
  const reads: Promise<unknown>[] = [loadAdminOverview()];
  if (accountId && state.adminAccount.data?.id === accountId) {
    const ledgerQuery = managementLocations.get('adminCredits')?.split('?')[1] ?? '';
    reads.push(loadAdminAccount(accountId), loadAdminCredits(accountId, ledgerQuery));
  }
  if (state.adminAccounts.status !== 'idle') {
    const previous = managementLocations.get('adminAccounts') ?? '/admin/accounts';
    reads.push(loadAdminAccounts(previous.split('?')[1] ?? ''));
  }
  if (state.adminAudit.status !== 'idle') reads.push(loadAdminAudit(managementLocations.get('adminAudit')?.split('?')[1] ?? ''));
  if (path.startsWith('/admin/billing/')) reads.push(loadAdminBilling(), loadBillingPlans());
  await Promise.allSettled(reads);
  return result;
}

async function billingRedirect(path: string, body: unknown): Promise<void> {
  const generation = authGeneration;
  const result = BillingRedirectSchema.parse(await request(path, {body, csrf: session().csrfToken}));
  if (generation !== authGeneration) throw new Error('The account changed before the billing page opened.');
  const destination = new URL(result.url);
  if (destination.protocol !== 'https:' || !['checkout.stripe.com', 'billing.stripe.com'].includes(destination.hostname)
    || destination.username || destination.password) throw new Error('The billing service returned an unexpected payment destination.');
  const failures: unknown[] = [];
  try {await platform().openAccountPage(destination.href);}
  catch (error) {failures.push(error);}
  if (platform().kind === 'desktop') {
    if (generation !== authGeneration) failures.push(new Error('The account changed while the billing window was open.'));
    else {
      const reads = await Promise.allSettled([loadAccountBilling(), refreshAccountBalance()]);
      failures.push(...reads.flatMap(result => result.status === 'rejected' ? [result.reason] : []));
    }
  }
  if (failures.length === 1) throw failures[0];
  if (failures.length > 1) throw new AggregateError(failures, 'The billing window and account status could not be completed.',
    {cause: failures.map(errorDetails)});
}
function config(): AppConfig {
  if (state.config.status !== 'ready' || !state.config.data) throw new Error('Load the service chain configuration before connecting a wallet.');
  return state.config.data;
}
export function currentSession(): Session | null {
  return state.session.status === 'ready' ? state.session.data : null;
}
export function session(): Session {
  const authenticated = currentSession();
  if (!authenticated) throw new Error('Sign in to your account first.');
  return authenticated;
}
async function operation<T>(label: string, callback: () => Promise<T>, scope: NonNullable<HermesState['operation']['scope']> = 'account'): Promise<T> {
  if (state.operation.label) throw new Error(`Wait for the current operation: ${state.operation.label}`);
  publish({operation: {label, error: null, scope}});
  try {return await callback();}
  catch (error) {publish({operation: {label: null, error: errorDetails(error), scope}}); throw error;}
  finally {if (state.operation.label === label) publish({operation: {...state.operation, label: null}});}
}
function clearAccount(): void {
  accountPreparationGeneration++;
  accountSettingsRead = null;
  managementGeneration++;
  managementLocations.clear();
  accountBalanceGeneration++;
  accountVirtualMachineGeneration++;
  accountDataResetGeneration++;
  accountDataResetRequestId = accountDataResetObservedId = accountDataResetFinishedId = null;
  authGeneration++; accountSettingsGeneration++; accountProfileGeneration++; whatsappGeneration++; googleGeneration++; discordGeneration++; nativeRuntimeGeneration++; nativeSessionsGeneration++;
  accountApplicationsGeneration++; applicationLogsGeneration++;
  closeHermes();
  publish({...adminResources(), ...paymentResources(), ...merchantResources(), commerce: idle<CommerceWorkspace>(), commerceEarnings: idle<CommerceEarnings>(), domains: idle<DomainAccount>(), billingPlans: idle<BillingPlans>(), accountBilling: idle<AccountBilling>(),
    accountPreparation: idle<AccountPreparation>(),
    accountVirtualMachine: idle<AccountVirtualMachineStatus>(),
    accountDataReset: idle<AccountDataResetStatus>(),
    accountSettings: idle<AccountSettings>(), accountProfile: idle<AccountProfile>(), accountBalance: idle<AccountCreditBalance>(), whatsapp: idle<WhatsAppStatus>(), google: idle<GoogleStatus>(), discord: idle<DiscordStatus>(),
    accountApplications: idle<AccountApplications>(), applicationLogs: idle<ApplicationLogs>(), applicationLogSlug: null,
    nativeRuntime: idle<NativeRuntimeStatus>(), nativeSessions: idle<NativeSessionMapping[]>(), ...nativeResources(),
    nativeTarget: null, nativeSelectedAgentId: null});
}
async function loadSession(): Promise<Session | null> {
  try {return await request<Session>('/auth/session');}
  catch (error) {
    if (error instanceof ApiError && error.details.status === 401 && error.details.code !== 'desktop_account_page_session_ended') return null;
    throw error;
  }
}
function publishSession(resource: Resource<Session | null>): void {
  const previous = state.session.data;
  const next = resource.data;
  platform().accountChanged(next?.accountId ?? null);
  publish({session: resource});
  if (previous?.accountId !== next?.accountId || previous?.csrfToken !== next?.csrfToken) {
    window.dispatchEvent(new CustomEvent('eightbots-account-changed', {detail: next?.accountId ?? null}));
    if(resource.status==='ready')void loadBillingPlans().catch(()=>undefined);
  }
}
function acceptSession(next: Session | null): void {
  sessionLoadGeneration++;
  if (next === null || next.authMethod !== 'wallet') {
    stopWalletWatch?.(); stopWalletWatch = null;
    publish({walletAddress: null});
  }
  const previous = state.session.data;
  // Automatic expiry keeps unsent inputs under their account's storage key.
  if (previous && next && previous.accountId !== next.accountId) clearChatDrafts(previous.accountId);
  if (next || previous) clearRegistrationDraft();
  if (previous?.adminRole !== next?.adminRole) {
    managementGeneration++;
    managementLocations.clear();
    publish(adminResources());
  }
  if (next === null || previous?.accountId !== next.accountId || previous.address !== next.address ||
      previous.chainId !== next.chainId || previous.csrfToken !== next.csrfToken) clearAccount();
  publishSession(ready(next));
}
async function refreshSession(): Promise<void> {
  if (sessionRead?.accountGeneration === authGeneration && sessionRead.generation === sessionLoadGeneration) return sessionRead.promise;
  const generation = ++sessionLoadGeneration;
  const accountGeneration = authGeneration;
  // Keep the current screen mounted while rechecking a resolved session,
  // including a signed-out form. Protected requests wait for this read;
  // acceptSession clears the account if identity changed.
  if (state.session.status !== 'ready') publish({session: {status: 'loading', data: null, error: null}});
  const promise = (async () => {
    try {
      const authenticated = await loadSession();
      if (generation === sessionLoadGeneration && accountGeneration === authGeneration) acceptSession(authenticated);
    } catch (error) {
      if (generation === sessionLoadGeneration && accountGeneration === authGeneration) {
        clearAccount();
        publishSession({status: 'error', data: null, error: errorDetails(error)});
      }
      throw error;
    }
  })();
  const pending = {accountGeneration, generation, promise};
  sessionRead = pending;
  try {await promise;}
  finally {if (sessionRead === pending) sessionRead = null;}
}
async function revalidateSession(): Promise<Session | null> {
  await refreshSession();
  return state.session.data;
}
async function loadConfig(): Promise<AppConfig> {
  const result = await request<AppConfig>('/config');
  if (result.publicOrigin !== platform().publicOrigin || `${result.basePath}/` !== import.meta.env.BASE_URL) {
    throw new Error('The browser origin or application path differs from the API configuration.');
  }
  return result;
}
async function refreshAccount(): Promise<void> {
  const path = currentLocation().pathname.slice(import.meta.env.BASE_URL.replace(/\/$/, '').length);
  const parameters = new URLSearchParams(currentLocation().search);
  if (!state.session.data?.onboarding.workspaceAdmitted || path === '/setup' || path.startsWith('/billing/payments/') || path === '/register' || (path === '/subscribe' && parameters.get('new_signup') === '1') ||
      (path === '/billing' && parameters.get('activate') === '1')) {
    // Cold provisioning owns the runtime lock. Setup polls its durable host
    // record instead of queueing file, WhatsApp and runtime reads behind boot.
    await Promise.allSettled([refreshAccountProfile(), refreshAccountPreparation()]);
    return;
  }
  // Conversation metadata and runtime health have independent read resources.
  await Promise.allSettled([refreshAccountSettings(), refreshAccountProfile(), refreshAccountBalance(), refreshWhatsApp(), refreshNativeSessions(true)]);
}
async function refreshAccountBalance(): Promise<AccountCreditBalance> {
  session();
  const generation = authGeneration, balanceGeneration = ++accountBalanceGeneration;
  return query('accountBalance', async () => AccountCreditBalanceSchema.parse(await request('/account/balance')),
    () => generation === authGeneration && balanceGeneration === accountBalanceGeneration);
}
let checkingResumedPage=false;
function refreshAccountOnFocus(): void {
  if (document.visibilityState !== 'visible'||checkingResumedPage) return;
  checkingResumedPage=true;
  void (async () => {
    const idle=()=>!state.operation.label&&!state.nativeDetail.data?.state.running&&!state.nativeDetail.data?.state.resuming&&!state.nativeDetail.data?.agents.some(agent=>agent.running||agent.resuming);
    if(await reloadChangedFrontend(idle))return;
    await refreshSession();
    const generation = authGeneration;
    if (accountRefresh?.accountGeneration === generation) return;
    const promise = (async () => {
      if (state.session.data) await refreshAccount();
      if (generation !== authGeneration) return;
      const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
      const path = currentLocation().pathname.slice(basePath.length);
      if (platform().kind === 'desktop' && state.session.data) {
        await Promise.allSettled([loadAccountBilling(), loadBillingPayments(),
          ...(path === '/settings' ? [refreshGoogle(), refreshDiscord()] : [])]);
        if (generation !== authGeneration) return;
      }
      if (path === '/subscribe' || path === '/pricing') await loadBillingPlans();
      if (path === '/billing' && state.session.data) await Promise.allSettled([loadAccountBilling(), loadBillingPlans(),loadBillingPaymentOptions(), refreshAccountBalance()]);
      if (path === EARNINGS_PATH && state.session.data) await loadCommerceEarnings();
      if (path === '/account/crypto-payments' && state.session.data) await Promise.allSettled([loadCommerce(),loadMerchantCrypto(),loadMerchantCryptoOptions()]);
      if (path === '/account/payments' && state.session.data) await Promise.allSettled([loadCommerce(),loadMerchantCard(),loadMerchantCardOptions()]);
      if ((path === '/admin' || path.startsWith('/admin/')) && state.session.data) {
        if (!state.session.data?.adminRole) return;
        await loadAdminAccess();
        if (generation !== authGeneration) return;
        const search = window.location.search;
        const account = path.match(/^\/admin\/accounts\/([0-9a-f-]+)$/);
        if (account?.[1]) await Promise.allSettled([loadAdminAccount(account[1]), loadAdminCredits(account[1], search)]);
        else if (path === '/admin/accounts') await loadAdminAccounts(search);
        else if (path === '/admin/usage') await loadAdminUsage(search);
        else if (path === '/admin/catalog') await loadAdminCatalog();
        else if (path === '/admin/mail-sender' && state.adminAccess.data?.role === 'owner') await loadAdminMailSender();
        else if (path === '/admin/operations') await loadAdminHealth();
        else if (path === '/admin/audit') await loadAdminAudit(search);
        else if (path === '/admin/billing') await loadAdminBilling();
        else await loadAdminOverview();
      }
    })();
    const pending = {accountGeneration: generation, promise};
    accountRefresh = pending;
    void promise.catch(() => undefined).finally(() => {if (accountRefresh === pending) accountRefresh = null;});
  })().catch(error => publish({operation:{label:null,error:errorDetails(error),scope:'account'}})).finally(()=>{checkingResumedPage=false;});
}
async function authenticateWithPassword(path: '/auth/login' | '/auth/register' | '/auth/setup-login',
  email: string, password: string, token?: string, planId?: string): Promise<Session> {
  const credentials = PasswordCredentialsSchema.parse({email, password});
  if (platform().kind === 'desktop') {
    if (path === '/auth/setup-login') throw new Error('Password setup for an existing account is not available in this app.');
    if (path === '/auth/register') await platform().registerWithPassword(credentials.email, credentials.password);
    else await platform().loginWithPassword(credentials.email, credentials.password);
    return readDesktopSignIn();
  }
  const authenticated = await request<Session>(path, {body: {...credentials, ...(token === undefined ? {} : {token}), ...(planId === undefined ? {} : {planId})}});
  acceptSession(authenticated);
  // Authentication is complete. Files, WhatsApp and usage own their loading and
  // error states; they must not hold the sign-in form or every account action.
  void refreshAccount();
  return authenticated;
}
async function readDesktopSignIn(): Promise<Session> {
  sessionLoadGeneration++;
  await refreshSession();
  const authenticated = session();
  void refreshAccount();
  return authenticated;
}
async function refreshNativeSessions(refreshRuntime = false): Promise<NativeSessionMapping[]> {
  session();
  const generation = authGeneration;
  const sessionsGeneration = ++nativeSessionsGeneration;
  if (refreshRuntime) void refreshHermesRuntime().catch(() => undefined);
  return query('nativeSessions', async () => {
    const snapshot = await request<{sessions: NativeSessionMapping[]}>('/hermes/sessions?includeRuntime=0');
    return snapshot.sessions;
  },
    () => generation === authGeneration && sessionsGeneration === nativeSessionsGeneration);
}
async function refreshAccountSettings(force = false): Promise<AccountSettings> {
  session();
  const generation = authGeneration;
  if (!force && accountSettingsRead?.accountGeneration === generation && accountSettingsRead.generation === accountSettingsGeneration) {
    return accountSettingsRead.promise;
  }
  const settingsGeneration = ++accountSettingsGeneration;
  const promise = query('accountSettings', () => request<AccountSettings>('/account/settings'),
    () => generation === authGeneration && settingsGeneration === accountSettingsGeneration, true);
  accountSettingsRead = {accountGeneration: generation, generation: settingsGeneration, promise};
  try {return await promise;}
  finally {if (accountSettingsRead?.generation === settingsGeneration) accountSettingsRead = null;}
}
async function refreshAccountPreparation(signal?: AbortSignal): Promise<AccountPreparation> {
  session();
  const generation = authGeneration, version = ++accountPreparationGeneration;
  return query('accountPreparation', async () => AccountPreparationSchema.parse(await request('/account/preparation',
    {signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(15_000)]) : AbortSignal.timeout(15_000)})),
  () => generation === authGeneration && version === accountPreparationGeneration);
}

async function activateBillingPlan(planId: string, requestId: string, signal?: AbortSignal): Promise<AccountBilling> {
  const generation = authGeneration;
  const snapshot = AccountBillingSchema.parse(await request('/account/billing/activate', {
    body: {planId, requestId}, csrf: session().csrfToken,
    signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(45_000)]) : AbortSignal.timeout(45_000),
  }));
  if (generation !== authGeneration) throw new Error('The account changed before plan activation completed.');
  managementVersions.set('accountBilling', (managementVersions.get('accountBilling') ?? 0) + 1);
  publish({accountBilling: ready(snapshot)});
  await refreshSession();
  return snapshot;
}

/** Resume from durable server records. A failed write is never silently replayed. */
async function setupAccount(planId: string, requestId: string, signal: AbortSignal, retry: boolean) {
  session();
  const generation = authGeneration;
  const check = () => {
    signal.throwIfAborted();
    if (generation !== authGeneration) throw new Error('The account changed during setup.');
  };
  const [plans, billing] = await Promise.all([loadBillingPlans(), loadAccountBilling()]);
  check();
  if (!plans.plans.some(plan => plan.id === planId) && billing.membership?.planId !== planId && billing.subscription?.planId!==planId && billing.paidSetup?.planId!==planId)
    throw new Error('Your selected plan is no longer available. Choose another plan.');
  const paid=billing.paidSetup?.planId===planId||(billing.subscription?.status==='active'&&billing.subscription.planId===planId&&billing.subscription.currentPeriodEnd!==null&&new Date(billing.subscription.currentPeriodEnd)>new Date());
  if(!paid && billing.membership?.planId!==planId){
    if (!billing.configuration.planActivationEnabled || (billing.membership && billing.membership.planId !== planId))return {billing:true as const};
    if (!billing.membership) await activateBillingPlan(planId, requestId, signal);
  }
  await refreshSession();
  check();
  let preparation = await refreshAccountPreparation(signal);
  if (retry && preparation.state === 'error') {
    preparation = AccountPreparationSchema.parse(await request('/account/prepare', {
      body: {}, csrf: session().csrfToken, signal: AbortSignal.any([signal, AbortSignal.timeout(15_000)]),
    }));
    check();
    accountPreparationGeneration++;
    publish({accountPreparation: ready(preparation)});
  }
  while (preparation.state !== 'ready') {
    if (preparation.state === 'error') throw new ApiError({name: 'AccountSetupError',
      message: 'Your team setup needs another attempt. Your account and completed work are saved.', cause: preparation.error});
    // Durable pending/running work keeps updating until ready, error or navigation.
    // A capacity queue is not a failed setup and needs no customer retry.
    await abortableDelay(2000, signal);
    check();
    preparation = await refreshAccountPreparation(signal);
    check();
  }
  const [balance] = await Promise.all([refreshAccountBalance(), refreshHermesRuntime(), refreshNativeSessions(), refreshAccountSettings(true),loadAccountBilling()]);
  check();
  if (balance.status !== 'ready') throw new Error('Your plan was selected, but its credits could not be confirmed. Check setup to continue.');
  const mapping = await openHermes(state.nativeSessions.data?.[0]?.sessionId ?? null);
  check();
  if (!mapping) throw new Error('Your first chat could not open. Check setup to reconnect.');
  return {billing: false as const, sessionId: mapping.sessionId};
}
async function refreshAccountVirtualMachine(signal?: AbortSignal): Promise<AccountVirtualMachineStatus> {
  session();
  const generation = authGeneration, vmGeneration = ++accountVirtualMachineGeneration;
  return query('accountVirtualMachine', async () => AccountVirtualMachineStatusSchema.parse(
    await request('/account/virtual-machine', signal ? {signal} : {})),
  () => generation === authGeneration && vmGeneration === accountVirtualMachineGeneration && !signal?.aborted);
}
async function recoverAccountVirtualMachine(): Promise<AccountVirtualMachineStatus> {
  const authenticated = session(), generation = authGeneration;
  accountVirtualMachineGeneration++;
  const status = AccountVirtualMachineStatusSchema.parse(await request('/account/virtual-machine/recover',
    {body: {}, csrf: authenticated.csrfToken}));
  if (generation !== authGeneration) throw new Error('The account changed before VM recovery was requested.');
  accountVirtualMachineGeneration++;
  publish({accountVirtualMachine: ready(status)});
  return status;
}
function acceptAccountDataReset(status: AccountDataResetStatus): void {
  if (status.state === 'idle' || status.requestId === null) return;
  const knownReset = accountDataResetObservedId === status.requestId || accountDataResetRequestId === status.requestId;
  accountDataResetRequestId = status.state === 'complete' ? null : status.requestId;
  if ((status.state !== 'complete' || knownReset) && accountDataResetObservedId !== status.requestId) {
    accountDataResetObservedId = status.requestId;
    closeHermes();
    accountSettingsGeneration++; nativeRuntimeGeneration++; nativeSessionsGeneration++;
    whatsappGeneration++; googleGeneration++; accountApplicationsGeneration++; applicationLogsGeneration++;
    publish({accountSettings: idle<AccountSettings>(), nativeRuntime: idle<NativeRuntimeStatus>(),
      nativeSessions: idle<NativeSessionMapping[]>(), nativeTarget: null, whatsapp: idle<WhatsAppStatus>(),
      google: idle<GoogleStatus>(), accountApplications: idle<AccountApplications>(),
      applicationLogs: idle<ApplicationLogs>(), applicationLogSlug: null});
    const accountPath = currentLocation().pathname.slice(import.meta.env.BASE_URL.length - 1);
    if (['/workspace', '/agents', '/tasks', '/files', '/settings'].includes(accountPath)) {
      const parameters = new URLSearchParams(currentLocation().search);
      const selectedWork = ['chat', 'agent', 'role'];
      if (selectedWork.some(key => parameters.has(key))) {
        for (const key of selectedWork) parameters.delete(key);
        const search = parameters.toString();
        window.history.replaceState(window.history.state, '', `${currentLocation().pathname}${search ? `?${search}` : ''}${window.location.hash}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    }
  }
  if (status.state === 'complete' && accountDataResetFinishedId !== status.requestId) {
    accountDataResetFinishedId = status.requestId;
    if (knownReset) void Promise.allSettled([refreshAccount(), refreshGoogle(), refreshApplications(), refreshAccountVirtualMachine()]);
  }
}
async function refreshAccountDataReset(signal?: AbortSignal): Promise<AccountDataResetStatus> {
  session();
  const generation = authGeneration, resetGeneration = ++accountDataResetGeneration;
  const valid = () => generation === authGeneration && resetGeneration === accountDataResetGeneration && !signal?.aborted;
  const status = await query('accountDataReset', async () => AccountDataResetStatusSchema.parse(
    await request('/account/data-reset', signal ? {signal} : {})), valid);
  if (valid()) acceptAccountDataReset(status);
  return status;
}
async function clearAccountData(confirmation: string): Promise<AccountDataResetStatus> {
  if (confirmation !== 'confirm') throw new Error('Type confirm exactly to clear your account data.');
  const authenticated = session(), generation = authGeneration;
  const current = state.accountDataReset.data;
  const requestId = (current?.state === 'failed' || current?.state === 'running' ? current.requestId : null)
    ?? accountDataResetRequestId ?? crypto.randomUUID();
  accountDataResetRequestId = requestId;
  accountDataResetGeneration++;
  const status = AccountDataResetStatusSchema.parse(await request('/account/data-reset',
    {body: {confirmation, requestId}, csrf: authenticated.csrfToken}));
  if (generation !== authGeneration) throw new Error('The account changed before clearing its data was requested.');
  accountDataResetGeneration++;
  publish({accountDataReset: ready(status)});
  acceptAccountDataReset(status);
  return status;
}
async function changeAccountSettings(path: string, method: 'PUT' | 'DELETE', body?: unknown): Promise<AccountSettings> {
  const generation = authGeneration;
  accountSettingsGeneration++;
  const settings = await request<AccountSettings>(path, {method, ...(body === undefined ? {} : {body}), csrf: session().csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the settings were saved.');
  accountSettingsGeneration++;
  publish({accountSettings: ready(settings)});
  return settings;
}

async function refreshAccountProfile(): Promise<AccountProfile> {
  session();
  const generation = authGeneration;
  const profileGeneration = ++accountProfileGeneration;
  return query('accountProfile', () => request<AccountProfile>('/account/profile'),
    () => generation === authGeneration && profileGeneration === accountProfileGeneration);
}

async function changeAccountUsername(username: string, expectedUsername: string): Promise<AccountProfile> {
  const authenticated = session();
  const generation = authGeneration;
  accountProfileGeneration++;
  const profile = await request<AccountProfile>('/account/username',
    {method: 'PUT', body: {username, expectedUsername}, csrf: authenticated.csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the username was saved.');
  accountProfileGeneration++;
  publish({accountProfile: ready(profile)});
  accountApplicationsGeneration++;
  publish({accountApplications: idle<AccountApplications>()});
  void refreshApplications().catch(() => undefined);
  return profile;
}

async function refreshApplications(): Promise<AccountApplications> {
  session();
  const generation = authGeneration;
  const applicationsGeneration = ++accountApplicationsGeneration;
  return query('accountApplications', () => request<AccountApplications>('/account/applications'),
    () => generation === authGeneration && applicationsGeneration === accountApplicationsGeneration);
}

async function changeApplication(path: string, body: unknown): Promise<ApplicationStatus> {
  const authenticated = session();
  const generation = authGeneration;
  accountApplicationsGeneration++;
  try {
    const saved = await request<ApplicationStatus>(path, {method: 'POST', body, csrf: authenticated.csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before the service was updated.');
    accountApplicationsGeneration++;
    const current = state.accountApplications.data;
    if (current) publish({accountApplications: ready({...current,
      applications: current.applications.some(item => item.slug === saved.slug)
        ? current.applications.map(item => item.slug === saved.slug ? saved : item)
        : [...current.applications, saved]})});
    return saved;
  } finally {
    if (generation === authGeneration) void refreshApplications().catch(() => undefined);
  }
}

async function readApplicationLogs(slug: string): Promise<ApplicationLogs> {
  session();
  const generation = authGeneration;
  const logsGeneration = ++applicationLogsGeneration;
  publish({applicationLogSlug: slug, applicationLogs: idle<ApplicationLogs>()});
  return query('applicationLogs', async () => {
    const logs = await request<ApplicationLogs>(`/account/applications/${encodeURIComponent(slug)}/logs`);
    if (logs.slug !== slug) throw new Error('The service returned logs for a different application.');
    return logs;
  }, () => generation === authGeneration && logsGeneration === applicationLogsGeneration);
}

async function refreshWhatsApp(): Promise<WhatsAppStatus> {
  session();
  const generation = authGeneration;
  const connectionGeneration = ++whatsappGeneration;
  return query('whatsapp', () => request<WhatsAppStatus>('/account/whatsapp'),
    () => generation === authGeneration && connectionGeneration === whatsappGeneration);
}

async function changeWhatsApp(path: string, method: 'POST' | 'DELETE', body: {sessionId?: string} = {}): Promise<WhatsAppStatus> {
  const authenticated = session();
  const generation = authGeneration;
  whatsappGeneration++;
  const status = await request<WhatsAppStatus>(path, {method, body, csrf: authenticated.csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the WhatsApp connection was updated.');
  whatsappGeneration++;
  publish({whatsapp: ready(status)});
  return status;
}

async function abortableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return;
  await new Promise<void>(resolve => {
    const finish = () => {clearTimeout(timer); signal.removeEventListener('abort', finish); resolve();};
    const timer = setTimeout(finish, milliseconds);
    signal.addEventListener('abort', finish, {once: true});
  });
}

async function readNativeWithReconnect<T>(read: () => Promise<T>, signal: AbortSignal,
  valid: () => boolean, onInterruption: (error: unknown) => void): Promise<T | null> {
  let failures = 0;
  while (valid()) {
    try {
      const result = await read();
      return valid() ? result : null;
    } catch (error) {
      if (!valid()) return null;
      if (!(error instanceof ApiError) || (!['NetworkError', 'RequestTimeout'].includes(error.details.name) &&
          ![502, 503, 504].includes(error.details.status ?? 0))) throw error;
      // Only reads use this owner. Preserve the caller's cursor and visible
      // failure until an actual response succeeds; never replay a write.
      onInterruption(error);
      await abortableDelay(Math.min(30_000, 1000 * 2 ** Math.min(++failures, 5)), signal);
    }
  }
  return null;
}

async function refreshGoogle(): Promise<GoogleStatus> {
  session();
  const generation = authGeneration;
  const readGeneration = ++googleGeneration;
  return query('google', async () => GoogleStatusSchema.parse(await request('/account/google')),
    () => generation === authGeneration && readGeneration === googleGeneration);
}
async function authorizeGoogle(path: string, body: unknown): Promise<void> {
  return authorizeIdentity('google', path, body);
}
async function refreshDiscord(): Promise<DiscordStatus> {
  session();
  const generation = authGeneration, readGeneration = ++discordGeneration;
  return query('discord', async () => DiscordStatusSchema.parse(await request('/account/discord')),
    () => generation === authGeneration && readGeneration === discordGeneration);
}
async function authorizeIdentity(provider: 'google' | 'discord', path: string, body: unknown): Promise<void> {
  if (platform().kind === 'desktop') {
    if (path.startsWith('/auth/')) throw new Error(`${provider === 'google' ? 'Google' : 'Discord'} sign-in is not available in this app. Use your EightBots email and password.`);
    throw new Error(provider === 'google' ? 'Google does not allow sign-in in this app’s embedded window.'
      : 'Discord account connections are not available in this app.');
  }
  const generation = authGeneration;
  const result = await request<{authorizationUrl: string}>(path, {body,
    ...(path.startsWith('/account/') ? {csrf: session().csrfToken} : {})});
  if (generation !== authGeneration) throw new Error('The account changed before authorization opened.');
  const target = new URL(result.authorizationUrl);
  const expected = provider === 'google' ? 'https://accounts.google.com/o/oauth2/v2/auth' : 'https://discord.com/oauth2/authorize';
  if (`${target.origin}${target.pathname}` !== expected || target.username || target.password) {
    throw new Error('The authorization address is invalid.');
  }
  window.location.assign(target.href);
}

async function refreshHermesRuntime(): Promise<NativeRuntimeStatus> {
  session();
  const generation = authGeneration;
  const runtimeGeneration = ++nativeRuntimeGeneration;
  return query('nativeRuntime', () => request<NativeRuntimeStatus>('/hermes/runtime'),
    () => authGeneration === generation && nativeRuntimeGeneration === runtimeGeneration);
}
async function refreshStartedWorkspace(accountGeneration: number, conversationGeneration?: number): Promise<void> {
  const valid = () => accountGeneration === authGeneration &&
    (conversationGeneration === undefined || conversationGeneration === nativeGeneration);
  if (!valid()) return;
  const results = await Promise.allSettled([refreshHermesRuntime(), refreshAccountSettings(true)]);
  if (!valid()) return;
  const failures = results.flatMap(result => result.status === 'rejected' ? [result.reason] : []);
  if (failures.length === 1) throw failures[0];
  if (failures.length > 1) throw new AggregateError(failures, 'The workspace status and files could not be refreshed.',
    {cause: failures.map(errorDetails)});
}
function stopHermesTransport(): void {
  nativeGeneration++; nativeDetailGeneration++; nativeAgentGeneration++;
  nativeInputOrigins.clear();
  nativeOpening = null;
  nativePollController?.abort(); nativePollController = null;
  nativeCommunicationController?.abort(); nativeCommunicationController = null;
  nativeTokenController?.abort(); nativeTokenController = null;
  nativeTokenGeneration++;
}
function closeHermes(): void {
  stopHermesTransport();
  publish({...nativeResources(), nativeSelectedAgentId: null, nativeConnection: {status: 'closed', error: null}});
}
function nativeChatReadSignal(): AbortSignal {
  if (!nativeCommunicationController) throw new Error('The work chat connection is closed.');
  return nativeCommunicationController.signal;
}
async function readNativeAgent(sessionId: string, agentId: string): Promise<NativeAgentDetail> {
  const previous = state.nativeAgent.data?.agent.sessionId === agentId ? state.nativeAgent.data : null;
  const cursor = previous?.historyPage?.after;
  const detail = await request<NativeAgentDetail>(`/hermes/sessions/${encodeURIComponent(sessionId)}/agents/${encodeURIComponent(agentId)}${cursor ? `?after=${encodeURIComponent(cursor)}` : ""}`,
    {signal: nativeChatReadSignal()});
  if (detail.agent.sessionId !== agentId) throw new Error('The service returned a different agent conversation.');
  const current = state.nativeAgent.data;
  return cursor && current?.agent.sessionId === agentId ? {...detail, ...mergeHistoryPage(current, detail, 'newer')} : detail;
}
function applyNativeHistoryPage(page: NativeConversationPage, cursor?: string | null): void {
  const current = state.nativeConversation.data;
  const merged = current?.session.sessionId === page.session.sessionId
    ? current.historyPage && cursor ? mergeHistoryPage({...current, historyPage: current.historyPage}, page, 'newer')
      : {displayHistory: mergeNativeMessages(current.displayHistory, page.displayHistory), historyPage: page.historyPage}
    : {displayHistory: page.displayHistory, historyPage: page.historyPage};
  publish({nativeConversation: ready({session: page.session, ...merged})});
}
async function readInitialNativeHistory(sessionId: string, generation: number, signal: AbortSignal): Promise<null> {
  const detailGeneration = ++nativeDetailGeneration;
  const valid = () => !signal.aborted && nativeGeneration === generation;
  return query('nativeHistory', async () => {
    const page = await readNativeWithReconnect(() =>
      request<NativeConversationPage>(`/hermes/sessions/${encodeURIComponent(sessionId)}/history`, {signal}),
      signal, valid, error => publish({nativeHistory: {status: 'error', data: null, error: errorDetails(error)}}));
    if (page === null) return null;
    if (page.session.sessionId !== sessionId || page.session.runId !== null) throw new Error('The service returned another work chat history.');
    if (valid() && nativeDetailGeneration === detailGeneration) applyNativeHistoryPage(page);
    return null;
  }, valid);
}
function refreshNativeDetail(sessionId: string, generation: number, fresh = false): Promise<NativeWorkDetail | null> {
  if (generation !== nativeGeneration) return Promise.resolve(null);
  const agentId = state.nativeSelectedAgentId;
  const includeAgentActivity = state.nativeWorkDetailsOpen;
  if (!fresh && nativeDetailRead?.generation === generation && nativeDetailRead.sessionId === sessionId && nativeDetailRead.agentId === agentId
    && nativeDetailRead.includeAgentActivity === includeAgentActivity) {
    return nativeDetailRead.promise;
  }
  // Polling joins the current read. Confirmed writes start one fresh read,
  // fencing older snapshots without allowing the next poll to supersede it.
  const promise = readNativeDetail(sessionId, generation, agentId, includeAgentActivity);
  const pending = {generation, sessionId, agentId, includeAgentActivity, promise};
  nativeDetailRead = pending;
  void promise.finally(() => {if (nativeDetailRead === pending) nativeDetailRead = null;}).catch(() => undefined);
  return promise;
}
async function readNativeDetail(sessionId: string, generation: number, agentId: string | null, includeAgentActivity: boolean): Promise<NativeWorkDetail | null> {
  const detailGeneration = ++nativeDetailGeneration;
  const requests: Promise<unknown>[] = [query('nativeDetail', async () => {
    const previous = state.nativeConversation.data?.session.sessionId === sessionId ? state.nativeConversation.data : null;
    const cursor = previous?.historyPage?.after;
    const parameters = new URLSearchParams();
    if (cursor) parameters.set('after', cursor);
    if (includeAgentActivity) parameters.set('includeAgentActivity', '1');
    const queryString = parameters.toString();
    const detail = await request<NativeSessionDetail>(`/hermes/sessions/${encodeURIComponent(sessionId)}${queryString ? `?${queryString}` : ''}`, {signal: nativeChatReadSignal()});
    if (detail.session.sessionId !== sessionId || detail.session.runId !== null) throw new Error('The service returned a different work chat.');
    if (!Number.isFinite(Date.parse(detail.observedAt)) || !Array.isArray(detail.openRequests)) throw new Error('The service returned an incomplete workspace snapshot.');
    const {displayHistory: _history,historyPage: _page,...work}=detail;
    if(nativeGeneration===generation&&nativeDetailGeneration===detailGeneration){
      applyNativeHistoryPage(detail,cursor);
    }
    return work;
  },
    () => nativeGeneration === generation && nativeDetailGeneration === detailGeneration, true)];
  if (agentId) {
    const agentGeneration = ++nativeAgentGeneration;
    requests.push(query('nativeAgent', () => readNativeAgent(sessionId, agentId),
      () => nativeGeneration === generation && nativeAgentGeneration === agentGeneration && state.nativeSelectedAgentId === agentId, true));
  }
  const results = await Promise.allSettled(requests);
  if (nativeGeneration !== generation) return null;
  if (results[0]?.status === 'rejected' && nativeDetailGeneration === detailGeneration) throw results[0].reason;
  // The selected transcript has its own error surface. It must not stop the team
  // snapshot, which owns live status, tasks, questions and usage for every actor.
  return nativeDetailGeneration === detailGeneration ? state.nativeDetail.data : null;
}
function pollHermes(sessionId: string, generation: number, initialDetail: Promise<NativeWorkDetail | null>): void {
  const controller = new AbortController();
  nativePollController = controller;
  const accountGeneration = authGeneration;
  const valid = () => !controller.signal.aborted && generation === nativeGeneration && accountGeneration === authGeneration;
  void (async () => {
    let cursor = 0;
    let epoch: string | null = null;
    let count = 0;
    let runtimeRefreshedAt = Date.now();
    let recentEvents: Record<string, unknown>[] = [];
    while (valid()) {
      const parameters = new URLSearchParams({after: String(cursor)});
      if (epoch !== null) parameters.set('epoch', epoch);
      const batch = await readNativeWithReconnect(() =>
        request<NativeEventBatch>(`/hermes/sessions/${encodeURIComponent(sessionId)}/events?${parameters}`,
          {signal: AbortSignal.any([controller.signal, AbortSignal.timeout(20_000)])}),
        controller.signal, valid, error => publish({nativeEvents: {
          status: 'error', data: state.nativeEvents.data, error: errorDetails(error)}}));
      if (batch === null) return;
      if (!valid()) return;
      if (batch.resetRequired || (epoch !== null && epoch !== batch.epoch)) recentEvents = [];
      recentEvents = [...recentEvents, ...batch.events].slice(-200);
      cursor = batch.cursor; epoch = batch.epoch;
      publish({nativeEvents: ready({...batch, events: recentEvents})});
      const stateEvents = batch.events.some(event => !['swarm.tokens','session.usage','communication.update'].includes(String(event.type)));
      if (count++ === 0) {
        await initialDetail.catch(() => undefined); // nativeDetail owns this failure.
        if (stateEvents || batch.resetRequired) await refreshNativeDetail(sessionId, generation).catch(() => undefined);
      } else if (count % 5 === 0 || stateEvents || batch.resetRequired) {
        await refreshNativeDetail(sessionId, generation).catch(() => undefined);
      }
      if (!valid()) return;
      if (document.visibilityState === 'visible' && Date.now() - runtimeRefreshedAt >= 30_000) {
        await refreshNativeSessions(true).catch(() => undefined);
        // Chat-list, runtime and snapshot failures retain their own error
        // resources. They must not permanently stop this independent reader.
        runtimeRefreshedAt = Date.now();
        if (!valid()) return;
      }
      await abortableDelay(1200, controller.signal);
    }
  })().catch(error => {
    if (valid()) publish({nativeEvents:{status:'error',data:state.nativeEvents.data,error:errorDetails(error)}});
  });
}

async function readCommunication(sessionId:string,generation:number,signal:AbortSignal,after:string|null=null):Promise<NativeCommunicationOutput|null>{
  const output=await request<NativeCommunicationOutput>(`/hermes/sessions/${encodeURIComponent(sessionId)}/communication${after?`?after=${encodeURIComponent(after)}`:''}`,
    {signal});
  if(signal.aborted||generation!==nativeGeneration)return null;
  if(output.sessionId!==sessionId||output.session.sessionId!==sessionId)throw new Error('Communication output belongs to another conversation.');
  const previous=state.nativeConversation.data;
  if(previous&&previous.session.sessionId!==sessionId)throw new Error('The active conversation changed before communication output was applied.');
  const {displayHistory,session:observedSession,...view}=output;
  const completedReply = after && platform().kind === 'desktop' && document.visibilityState === 'hidden'
    ? displayHistory.find(row => row.role === 'assistant' && row.communication?.callId && !row.communication.incomplete &&
      state.nativeCommunication.data?.streams.some(stream => stream.call_id === row.communication!.callId) &&
      !previous?.displayHistory.some(saved => saved.communication?.callId === row.communication!.callId)) : undefined;
  // NativeConversation owns every saved root message, even before work metadata
  // loads. The communication resource holds only live output and read receipts.
  const removed=new Set(view.receipts.filter(receipt=>receipt.message_removed).map(receipt=>receipt.request_id));
  const retained=previous?.displayHistory.filter(row=>!row.communication||!removed.has(row.communication.requestId))??[];
  const merged=mergeNativeMessages(retained,displayHistory);
  const savedCalls=new Set(merged.map(row=>row.communication?.callId).filter(Boolean));
  const presentCalls=new Set(view.streams.map(stream=>stream.call_id));
  const pendingPageStreams=output.historyPage.hasNewer?(state.nativeCommunication.data?.streams??[])
    .filter(stream=>!presentCalls.has(stream.call_id)&&!savedCalls.has(stream.call_id)&&!removed.has(stream.request_id)):[];
  publish({nativeConversation:ready({session:previous?.session??observedSession,displayHistory:merged,historyPage:previous?.historyPage??null}),
    nativeCommunication:ready({...view,streams:[...view.streams,...pendingPageStreams]}),
    nativeConnection:{status:'open',error:null}});
  applyNativeInputSelection(output);
  if (completedReply && state.session.data) window.dispatchEvent(new CustomEvent('eightbots-native-reply', {
    detail: {accountId: state.session.data.accountId, sessionId},
  }));
  return output;
}

function nativeInputLocation(): string {
  const location = currentLocation();
  return `${location.pathname}${location.search}`;
}

function applyNativeInputSelection(output: NativeCommunicationOutput): void {
  for (const receipt of output.receipts) {
    const origin = nativeInputOrigins.get(receipt.request_id);
    if (!origin) continue;
    if (origin.accountId !== state.session.data?.accountId || origin.agentId !== state.nativeSelectedAgentId ||
        origin.location !== nativeInputLocation()) {
      nativeInputOrigins.delete(receipt.request_id);
      continue;
    }
    if (receipt.status === 'queued' || receipt.status === 'running') continue;
    nativeInputOrigins.delete(receipt.request_id);
    if (receipt.message_removed || !receipt.selection) continue;
    // The native branch transaction supplied this target. Never interpret a
    // model reply, user text or a receipt from another tab as a navigation action.
    const path = currentLocation().pathname;
    nativeInputOrigins.clear();
    navigate(`${path}?chat=${encodeURIComponent(receipt.selection.session_id)}`);
    // The session-list resource owns and displays a failed refresh.
    void refreshNativeSessions().catch(() => undefined);
    return;
  }
}

function communicationReadError(error: unknown): void {
  publish({nativeCommunication: {status: 'error', data: state.nativeCommunication.data, error: errorDetails(error)},
    nativeConnection: {status: 'error', error: errorDetails(error)}});
}

function pollCommunication(sessionId:string,generation:number,initial:NativeCommunicationOutput,signal:AbortSignal):void {
  // The connection owns cancellation for initial history and communication.
  // Continuing its communication reader must not cancel the history read.
  const accountGeneration=authGeneration;
  const valid=()=>!signal.aborted&&generation===nativeGeneration&&accountGeneration===authGeneration;
  void (async()=>{
    let after:string|null=initial.historyPage.after;
    if(!initial.historyPage.hasNewer)await abortableDelay(1200,signal);
    while(valid()){
      const output:NativeCommunicationOutput|null=await readNativeWithReconnect(
        ()=>readCommunication(sessionId,generation,signal,after), signal, valid, communicationReadError);
      if(!valid()||output===null)return;
      after=output.historyPage.after??after;
      if(!output.historyPage.hasNewer)await abortableDelay(1200,signal);
    }
  })().catch(error=>{
    if(valid())communicationReadError(error);
  });
}
function streamNativeTokens(sessionId: string, generation: number): void {
  nativeTokenController?.abort();
  const tokenGeneration = ++nativeTokenGeneration;
  const controller = new AbortController();
  nativeTokenController = controller;
  const accountGeneration = authGeneration;
  const authenticated = state.session.data;
  const path = `/hermes/sessions/${encodeURIComponent(sessionId)}/tokens?credits=1`;
  const valid = () => !controller.signal.aborted && generation === nativeGeneration && accountGeneration === authGeneration && tokenGeneration === nativeTokenGeneration;
  publish({nativeTokens: {status: 'loading', data: null, error: null}});
  void (async () => {
    let failures = 0;
    while (valid()) {
      const attempt = new AbortController();
      const stopAttempt = () => attempt.abort(controller.signal.reason);
      controller.signal.addEventListener('abort', stopAttempt, {once: true});
      let deadline: ReturnType<typeof setTimeout> | null = null;
      const armDeadline = () => {
        if (deadline) clearTimeout(deadline);
        deadline = setTimeout(() => attempt.abort(new TokenTransportError('The token stream stopped responding.')), 30_000);
      };
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let receivedAt: number | null = null;
      let interrupted: unknown;
      let plannedRestart = false;
      try {
        armDeadline();
        let response: Response;
        try {
          response = await platform().fetch(`${base()}${path}`,
            {credentials: 'same-origin', headers: {accept: 'text/event-stream'}, signal: attempt.signal});
        } catch (error) {
          throw attempt.signal.aborted ? attempt.signal.reason : new TokenTransportError('The token connection could not open.', error);
        }
        if (!response.ok) {
          try {await responseBody(response);}
          catch (error) {
            if ([502, 503, 504].includes(response.status)) throw new TokenTransportError('The token service is not available.', errorDetails(error));
            throw error;
          }
        }
        if (!response.headers.get('content-type')?.startsWith('text/event-stream') || !response.body) {
          throw new Error('The token connection did not return an event stream.');
        }
        reader = response.body.getReader();
        const decoder = new EventStreamDecoder();
        while (valid()) {
          let next: ReadableStreamReadResult<Uint8Array>;
          try {next = await reader.read();}
          catch (error) {
            throw attempt.signal.aborted ? attempt.signal.reason : new TokenTransportError('The token connection was interrupted.', error);
          }
          if (next.done) throw new TokenTransportError('The token connection closed.');
          armDeadline();
          for (const event of decoder.push(next.value)) {
            const value: unknown = JSON.parse(event.data);
            if (event.event === 'reconnect') {
              NativeTokenReconnectSchema.parse(value);
              plannedRestart = true;
              throw new TokenTransportError('The token service is restarting.');
            }
            if (event.event === 'error') {
              if (!value || typeof value !== 'object' || !('message' in value) || typeof value.message !== 'string') {
                throw new Error(`Invalid token stream error: ${event.data}`);
              }
              throw new ApiError(value as ErrorDetails);
            }
            if (event.event === 'credits' || event.event === 'credits-error') {
              if (!valid()) return;
              accountBalanceGeneration++;
              if (event.event === 'credits') publish({accountBalance: ready(AccountCreditBalanceSchema.parse(value))});
              else {
                if (!value || typeof value !== 'object' || !('message' in value)) throw new Error('Invalid credit stream error.');
                publish({accountBalance: {status: 'error', data: null, error: value as ErrorDetails}});
              }
              continue;
            }
            if (event.event !== 'tokens') throw new Error(`Unknown token stream event: ${event.event}`);
            const snapshot = NativeTokenSnapshotSchema.parse(value);
            if (snapshot.sessionId !== sessionId) throw new Error('Token usage belongs to another work chat.');
            if (!valid()) return;
            const previous = state.nativeTokens.data;
            if (previous?.epoch === snapshot.epoch && previous.sequence > snapshot.sequence) continue;
            receivedAt ??= Date.now();
            publish({nativeTokens: ready(snapshot)});
          }
        }
      } catch (error) {
        interrupted = error;
      } finally {
        if (deadline) clearTimeout(deadline);
        controller.signal.removeEventListener('abort', stopAttempt);
        attempt.abort();
        reader?.releaseLock();
      }
      if (!valid()) return;
      rejectExpiredAccount(path, interrupted, authenticated, accountGeneration);
      if (!valid()) return;
      if (!(interrupted instanceof TokenTransportError)) throw interrupted;
      // A fresh initial snapshot alone does not reset a flapping connection's budget.
      if (receivedAt !== null && Date.now() - receivedAt >= 30_000) failures = 0;
      if (failures >= 5) throw interrupted;
      publish({nativeTokens: {status: 'loading', data: null, error: plannedRestart ? null : errorDetails(interrupted)}});
      const delay = Math.min(15_000, 1000 * 2 ** failures++) * (0.75 + Math.random() * 0.5);
      await abortableDelay(delay, controller.signal);
      await waitForTokenReconnect(controller.signal);
    }
  })().catch(error => {
    if (valid()) publish({nativeTokens: {status: 'error', data: null, error: errorDetails(error)}});
  });
}
async function waitForTokenReconnect(signal: AbortSignal): Promise<void> {
  if (signal.aborted || (document.visibilityState === 'visible' && navigator.onLine)) return;
  await new Promise<void>(resolve => {
    const finish = () => {
      if (!signal.aborted && (document.visibilityState !== 'visible' || !navigator.onLine)) return;
      document.removeEventListener('visibilitychange', finish);
      window.removeEventListener('online', finish);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    document.addEventListener('visibilitychange', finish);
    window.addEventListener('online', finish);
    signal.addEventListener('abort', finish, {once: true});
    finish();
  });
}
function reconnectNativeTokens(): void {
  session();
  const sessionId = state.nativeTarget?.sessionId;
  if (!sessionId || state.nativeConnection.status !== 'open') throw new Error('Connect to the work chat before reading its tokens.');
  streamNativeTokens(sessionId, nativeGeneration);
}
function resumeWorkspaceConnection(event: Event): void {
  if (event.type === 'pagehide' || event.type === 'freeze') {workspaceWasHidden = true; return;}
  if (document.visibilityState !== 'visible') {workspaceWasHidden = true; return;}
  if (!navigator.onLine) return;
  const resumed = workspaceWasHidden || event.type === 'online' || event.type === 'resume' ||
    (event.type === 'pageshow' && (event as PageTransitionEvent).persisted) ||
    (event.type === 'focus' && state.nativeConnection.status === 'error');
  workspaceWasHidden = false;
  if (!resumed || !state.session.data || state.nativeConnection.status === 'closed') return;
  // Mobile browsers suspend both fetches and sockets. Reopen the same saved
  // conversation once per resume; never replay a message or terminal input.
  if (Date.now() - workspaceResumedAt < 1000) return;
  workspaceResumedAt = Date.now();
  const accountGeneration = authGeneration, generation = nativeGeneration;
  const target = state.nativeTarget?.sessionId ?? null;
  void (async () => {
    await refreshSession();
    // Another tab can replace the shared login cookie while this page sleeps.
    // Only the unchanged account may reopen its previous conversation target.
    if (accountGeneration !== authGeneration || generation !== nativeGeneration || !state.session.data) return;
    await openHermes(target, true);
  })().catch(() => undefined);
}
async function openHermes(sessionId: string | null = null, reconnect = false): Promise<NativeSessionMapping | null> {
  session();
  if (nativeOpening?.accountGeneration === authGeneration && nativeOpening.generation === nativeGeneration && nativeOpening.sessionId === sessionId) {
    return nativeOpening.promise;
  }
  if (!reconnect && state.nativeConversation.data && (sessionId === null || state.nativeConversation.data.session.sessionId === sessionId) && state.nativeConnection.status === 'open') {
    return state.nativeConversation.data.session;
  }
  const promise = startHermesConnection(sessionId);
  const opening = {accountGeneration: authGeneration, generation: nativeGeneration, sessionId, promise};
  nativeOpening = opening;
  try {return await promise;}
  finally {if (nativeOpening === opening) nativeOpening = null;}
}
async function startHermesConnection(sessionId: string | null): Promise<NativeSessionMapping | null> {
  const sameTarget = sessionId !== null && state.nativeTarget?.sessionId === sessionId && state.nativeConversation.data?.session.sessionId === sessionId;
  // Account/session changes use closeHermes or clear these resources below.
  // Reconnecting the current target only replaces its transports, so a slow or
  // failed read leaves its last transcript visible with all writes gated closed.
  stopHermesTransport();
  const generation = nativeGeneration;
  const accountGeneration = authGeneration;
  publish({...(!sameTarget ? {...nativeResources(), nativeSelectedAgentId: null} : {
    nativeEvents: idle<NativeEventBatch>(), nativeMessageResult: idle<NativeMessageResult>(), nativeTokens: idle<NativeTokenSnapshot>()
  }), nativeTarget: {sessionId},
    nativeConnection: {status: 'connecting', error: null}});
  // Opening a chat still starts its stopped workspace. Preparation owns its
  // health error; readable messages must not wait for external services.
  const runtimeGeneration = ++nativeRuntimeGeneration;
  void query('nativeRuntime', () => request<NativeRuntimeStatus>('/hermes/runtime', {
    body: {}, csrf: session().csrfToken,
  }), () => generation === nativeGeneration && accountGeneration === authGeneration &&
    runtimeGeneration === nativeRuntimeGeneration).catch(() => undefined);
  try {
    const target = sessionId === null
      ? (await request<NativeSessionMapping>('/hermes/sessions', {body: {mode: 'ensure'}, csrf: session().csrfToken})).sessionId
      : sessionId;
    if (generation !== nativeGeneration || accountGeneration !== authGeneration) return null;
    publish({nativeTarget:{sessionId:target}});
    const controller=new AbortController();
    nativeCommunicationController=controller;
    // Read the saved transcript before starting the full team snapshot. Flash
    // admission and output stay independent of both reads.
    const initialHistory=readInitialNativeHistory(target,generation,controller.signal);
    const initialDetail=initialHistory.catch(() => undefined)
      .then(() => refreshNativeDetail(target,generation));
    void initialDetail.catch(() => undefined); // nativeDetail owns the visible error.
    const output=await readNativeWithReconnect(()=>readCommunication(target,generation,controller.signal), controller.signal,
      ()=>!controller.signal.aborted&&generation===nativeGeneration&&accountGeneration===authGeneration, communicationReadError);
    if(output===null||generation!==nativeGeneration||accountGeneration!==authGeneration)return null;
    // These readers publish their own errors. Neither account settings nor the
    // work snapshot controls communication admission.
    if (state.accountSettings.status === 'idle' || state.accountSettings.status === 'error')
      void refreshAccountSettings(true).catch(() => undefined);
    pollCommunication(target,generation,output,controller.signal);
    pollHermes(target, generation,initialDetail);
    streamNativeTokens(target, generation);
    if (state.nativeRuntime.status !== 'loading' && !state.nativeRuntime.data?.running) void refreshHermesRuntime().catch(() => undefined);
    // Reading the list has its own error surface and request generation.
    if(state.nativeSessions.status==='idle'||state.nativeSessions.status==='error')
      void refreshNativeSessions().catch(() => undefined);
    return output.session;
  } catch (error) {
    if (generation === nativeGeneration) publish({nativeConnection: {status: 'error', error: errorDetails(error)}});
    throw error;
  }
}
async function selectNativeAgent(agentId: string | null): Promise<void> {
  const mapping = state.nativeConversation.data?.session;
  if (!mapping) throw new Error('Open your Hermes conversation first.');
  if (state.nativeSelectedAgentId === agentId) return;
  nativeInputOrigins.clear();
  nativeAgentGeneration++;
  publish({nativeHistory: idle<null>(), nativeSelectedAgentId: agentId, nativeAgent: idle<NativeAgentDetail>(), nativeMessageResult: idle<NativeMessageResult>()});
  if (agentId) {
    const generation = nativeGeneration;
    const agentGeneration = nativeAgentGeneration;
    await query('nativeAgent', () => readNativeAgent(mapping.sessionId, agentId),
      () => generation === nativeGeneration && agentGeneration === nativeAgentGeneration && state.nativeSelectedAgentId === agentId);
  }
}
async function loadOlderNativeMessages(): Promise<void> {
  const target = requireNativeChatTarget();
  if (state.nativeHistory.status === 'loading') return;
  const selected = target.agentId === null ? state.nativeConversation.data : state.nativeAgent.data;
  const before = selected?.historyPage?.before;
  if (!before || !selected?.historyPage?.hasOlder) return;
  const generation = nativeGeneration;
  const valid = () => generation === nativeGeneration && state.nativeSelectedAgentId === target.agentId &&
    state.session.data?.accountId === target.accountId && state.nativeConversation.data?.session.sessionId === target.sessionId;
  const path = `/hermes/sessions/${encodeURIComponent(target.sessionId)}` +
    (target.agentId === null ? '/history' : `/agents/${encodeURIComponent(target.agentId)}`);
  await query('nativeHistory', async () => {
    const page = await request<NativeConversationPage | NativeAgentDetail>(`${path}?before=${encodeURIComponent(before)}`,
      {signal: AbortSignal.timeout(30_000)});
    if (!valid()) return null;
    if (target.agentId === null) {
      if (!('session' in page) || page.session.sessionId !== target.sessionId) throw new Error('The history page belongs to another chat.');
      const current = state.nativeConversation.data!;
      if(!current.historyPage)throw new Error('The work history changed while its earlier page was loading.');
      publish({nativeConversation: ready({...current, ...mergeHistoryPage({...current,historyPage:current.historyPage}, page, 'older')})});
    } else {
      if (!('agent' in page) || page.agent.sessionId !== target.agentId) throw new Error('The history page belongs to another agent.');
      const current = state.nativeAgent.data!;
      publish({nativeAgent: {...state.nativeAgent, data: {...current, ...mergeHistoryPage(current, page, 'older')}}});
    }
    return null;
  }, valid);
}

export type NativeChatTarget = {accountId: string; sessionId: string; agentId: string | null};
function requireNativeChatTarget(expected?: NativeChatTarget): NativeChatTarget {
  const authenticated = session();
  const detail = state.nativeConversation.data;
  if (!detail || state.nativeConnection.status !== 'open' || state.nativeCommunication.status === 'error' ||
      detail.session.sessionId !== state.nativeTarget?.sessionId) {
    throw new Error('Connect to your work chat before sending a message or attachment.');
  }
  const current = {accountId: authenticated.accountId, sessionId: detail.session.sessionId, agentId: state.nativeSelectedAgentId};
  if (expected && (expected.accountId !== current.accountId || expected.sessionId !== current.sessionId || expected.agentId !== current.agentId)) {
    throw new Error('The selected conversation changed. Return to the original conversation before sending this draft.');
  }
  return current;
}
async function sendNativeMessage(text: string, attachmentIds: string[] = [], expectedTarget?: NativeChatTarget, requestId: string = crypto.randomUUID(), recover = false): Promise<NativeMessageResult> {
  const target = requireNativeChatTarget(expectedTarget);
  const authenticated = session();
  const generation = nativeGeneration;
  const agentId = target.agentId;
  const path = `/hermes/sessions/${encodeURIComponent(target.sessionId)}` +
    (agentId === null ? '' : `/agents/${encodeURIComponent(agentId)}`);
  const valid = () => generation === nativeGeneration && state.nativeSelectedAgentId === agentId &&
    state.session.data?.accountId === target.accountId && state.nativeConversation.data?.session.sessionId === target.sessionId;
  const receipt = () => request<NativeMessageResult | null>(
    `/hermes/sessions/${encodeURIComponent(target.sessionId)}/messages/${encodeURIComponent(requestId)}`);
  publish({nativeMessageResult: {status: 'loading', data: state.nativeMessageResult.data, error: null}});
  nativeInputOrigins.set(requestId, {accountId: target.accountId, agentId, location: nativeInputLocation()});
  let result: NativeMessageResult;
  try {
    // A retry first reads the saved admission. Only explicit absence permits
    // submitting the same request again; a failed read cannot mean absence.
    let observed = recover ? await receipt() : null;
    if (observed === null) {
      requireNativeChatTarget(target);
      try {
        observed = await request<NativeMessageResult>(`${path}/messages`, {body: {text, attachmentIds, requestId},
          csrf: authenticated.csrfToken, signal: AbortSignal.timeout(30_000)});
      } catch (sendError) {
        if (!valid()) throw sendError;
        const status = errorDetails(sendError).status;
        if (status === 401 || status === 403) throw sendError;
        try {observed = await receipt();}
        catch (readError) {
          throw new AggregateError([sendError, readError], 'Message admission and receipt recovery failed.',
            {cause: [errorDetails(sendError), errorDetails(readError)]});
        }
        if (observed === null) throw sendError;
      }
    }
    result = observed;
    if (valid()) {
      // Native admission supplies the saved row and its original authoring time.
      const patch = nativeReceiptPatch(result, target, requestId);
      publish({...patch, nativeMessageResult: ready(result)});
    }
  } catch (error) {
    if (valid()) publish({nativeMessageResult: {status: 'error', data: null, error: errorDetails(error)}});
    throw error;
  }
  return result;
}
function nativeReceiptPatch(result: NativeMessageResult, target: NativeChatTarget, requestId: string): Partial<HermesState> {
  const receipt = result.receipt;
  if (!receipt) throw new Error('The message result has no admission receipt.');
  if (receipt.requestId !== requestId || receipt.sessionId !== target.sessionId || receipt.agentId !== target.agentId) {
    throw new Error('The native message receipt belongs to a different submission.');
  }
  if(!receipt.communication)throw new Error('The message has no communication admission receipt.');
  const message=receipt.message;
  if(!message)return {};
  if(message.role!=='user'||typeof message.row_id!=='number'||!Number.isSafeInteger(message.row_id)||message.row_id<1){
    throw new Error('The native message receipt has no valid saved user identity.');
  }
  // Communication inputs belong to the root conversation. An agent selection
  // is a target hint; it does not create another original in the child's history.
  const current=state.nativeConversation.data;
  if(current?.session.sessionId===target.sessionId)return {nativeConversation:ready({...current,
    displayHistory:mergeNativeMessages(current.displayHistory,[message])})};
  return {};
}
async function uploadChatAttachment(file: File, expectedTarget?: NativeChatTarget): Promise<ChatAttachment> {
  const target = requireNativeChatTarget(expectedTarget);
  const authenticated = session();
  const accountGeneration = authGeneration;
  const sessionId = target.sessionId;
  if (accountGeneration !== authGeneration) throw new Error('The account changed before the file could be uploaded.');
  requireNativeChatTarget(target);
  const attachment = ChatAttachmentSchema.parse(await request(`/hermes/sessions/${encodeURIComponent(sessionId)}/attachments`,
    {upload: {file, metadata: encodeURIComponent(JSON.stringify({name: file.name, mediaType: file.type, size: file.size}))},
      csrf: authenticated.csrfToken}));
  if (accountGeneration !== authGeneration) throw new Error('The account changed while the file was uploaded.');
  // The receipt belongs to the original draft even if another chat is now selected.
  return attachment;
}
function refreshAfterNativeAction(sessionId: string, generation: number): void {
  // The work query owns this error. Communication has a separate connection.
  void refreshNativeDetail(sessionId, generation, true).catch(() => undefined);
}

async function respondToNativeRequest(requestId: string,
  body: {result: Record<string, unknown>} | {cancel: true; message: string}): Promise<unknown> {
  const detail = state.nativeDetail.data, authenticated = session();
  if (!detail || state.nativeConnection.status !== 'open' || state.accountSettings.error !== null
    || state.nativeTarget?.sessionId !== detail.session.sessionId) throw new Error('Connect to your work chat before answering.');
  if (!detail.openRequests.some(item => item.id === requestId)) throw new Error('This request is no longer open in the workspace snapshot.');
  const generation = nativeGeneration, accountGeneration = authGeneration;
  try {
    const response = await request<{accepted: boolean}>(`/hermes/sessions/${encodeURIComponent(detail.session.sessionId)}/requests/${encodeURIComponent(requestId)}`,
      {body, csrf: authenticated.csrfToken});
    if (response.accepted !== true) throw new Error('Hermes did not confirm that it accepted this response.');
    return response;
  } finally {
    // Refresh expired, cancelled and concurrently answered requests as well as successful answers.
    if (generation === nativeGeneration && accountGeneration === authGeneration) refreshAfterNativeAction(detail.session.sessionId, generation);
  }
}

const loadCommerce=()=>readManagement('commerce','/account/commerce');
const loadCommerceEarnings=()=>readManagement('commerceEarnings','/account/commerce/earnings',CommerceEarningsSchema.parse);
const loadMerchantCard=()=>readManagement('merchantCard','/account/commerce/card-sales',MerchantCardWorkspaceSchema.parse);
const loadMerchantCardOptions=()=>readManagement('merchantCardOptions','/account/commerce/card-sales/options',MerchantCardOptionsSchema.parse);
const loadMerchantCrypto=()=>readManagement('merchantCrypto','/account/commerce/crypto-sales',MerchantCryptoWorkspaceSchema.parse);
const loadMerchantCryptoOptions=()=>readManagement('merchantCryptoOptions','/account/commerce/crypto-sales/options',MerchantCryptoOptionsSchema.parse);
function merchantMutation<T=unknown>(product:'payments'|'crypto',path:string,body:unknown,parse?:(value:unknown)=>T):Promise<T>{return operation('Update payments',async()=>{
  const generation=authGeneration,load=product==='payments'?loadMerchantCard:loadMerchantCrypto;let result:T;
  try{const response=await request<T>(`/account/commerce/${product==='payments'?'card':'crypto'}-sales${path}`,{body,csrf:session().csrfToken});result=parse?parse(response):response;}
  catch(error){if(generation===authGeneration)await load().catch(()=>undefined);throw error;}
  if(generation!==authGeneration)throw new Error('The signed-in account changed during this operation.');
  await load();return result;
},'commerce');}
async function loadDomains(){
  const signature=(data:DomainAccount)=>[...data.registrations,...data.renewals].map(row=>`${row.id}:${row.state}`).join('|');
  const before=state.domains.data?signature(state.domains.data):null;
 const result=await readManagement('domains','/account/domains');
  if(before!==signature(result)||state.accountBalance.status==='idle')await refreshAccountBalance();
 return result;
}
const loadAdminDomains=()=>readManagement('adminDomains','/admin/domains');
async function requestDomainAccountTransfer(id:string,body:unknown){const generation=authGeneration;
 return operation('Send domain transfer request',async()=>{
  await request(`/account/domains/${encodeURIComponent(id)}/account-transfer`,{body,csrf:session().csrfToken});
  if(generation!==authGeneration)throw new Error('The account changed during the transfer request.');
  await loadDomains();
 },'commerce');
}
async function decideDomainAccountTransfer(id:string,body:unknown){const generation=authGeneration;
 return operation('Save domain transfer decision',async()=>{
  await request(`/account/domain-account-transfers/${encodeURIComponent(id)}/decision`,{body,csrf:session().csrfToken});
  if(generation!==authGeneration)throw new Error('The account changed during the transfer decision.');
  await loadDomains();
 },'commerce');
}
 async function reviewDomainRenewal(id:string,body:unknown){return operation('Review domain renewal',async()=>{
  await request(`/admin/domains/renewals/${encodeURIComponent(id)}/review`,{body,csrf:session().csrfToken});await loadAdminDomains();
 },'admin');}
 async function reviewDomainTransfer(id:string,body:unknown){return operation('Review domain transfer protection',async()=>{
  await request(`/admin/domains/transfer-actions/${encodeURIComponent(id)}/review`,{body,csrf:session().csrfToken});await loadAdminDomains();
 },'admin');}
 async function renewDomain(id:string,body:unknown){const generation=authGeneration;
  return operation('Renew domain',async()=>{
   const result=await request(`/account/domains/${encodeURIComponent(id)}/renew`,{body,csrf:session().csrfToken});
   if(generation!==authGeneration)throw new Error('The account changed during renewal.');
   await loadDomains();return result;
  },'commerce');
 }
async function registerDomain(body:unknown){const generation=authGeneration;
  return operation('Register domain',async()=>{
    const result=await request<DomainRegistration>('/account/domains/register',{body,csrf:session().csrfToken});
    if(generation!==authGeneration)throw new Error('The account changed during registration.');
    await loadDomains();return result;
  },'commerce');
}
async function transferDomain(body:unknown){const generation=authGeneration;
 return operation('Transfer domain',async()=>{
  const result=await request<DomainRegistration>('/account/domains/transfer-in',{body,csrf:session().csrfToken});
  if(generation!==authGeneration)throw new Error('The account changed during the transfer request.');
  await loadDomains();return result;
 },'commerce');
}
async function cancelDomainTransfer(id:string){const generation=authGeneration;
 return operation('Cancel domain transfer',async()=>{
  await request(`/account/domains/${encodeURIComponent(id)}/cancel-transfer`,{body:{consent:true},csrf:session().csrfToken});
  if(generation!==authGeneration)throw new Error('The account changed during transfer cancellation.');
  await loadDomains();
 },'commerce');
}
async function reviewIncomingTransfer(id:string,body:unknown){return operation('Review incoming domain transfer',async()=>{
 await request(`/admin/domains/incoming-transfers/${encodeURIComponent(id)}/review`,{body,csrf:session().csrfToken});await loadAdminDomains();
},'admin');}
function billingAccountMutation(path:string,body:unknown){return operation('Update billing',async()=>{
  const generation=authGeneration;
  let billing:AccountBilling;
  try{billing=AccountBillingSchema.parse(await request(path,{body,csrf:session().csrfToken}));}
  catch(error){
    // A provider failure can follow a committed request. Read its saved state and preserve the action error.
    if(generation===authGeneration)await Promise.allSettled([loadAccountBilling(),loadBillingPaymentOptions()]);
    throw error;
  }
  if(generation!==authGeneration)throw new Error('The account changed during the billing update.');
  managementVersions.set('accountBilling',(managementVersions.get('accountBilling')??0)+1);
  publish({accountBilling:ready(billing)});
  await loadBillingPaymentOptions();return billing;
},'billing');}
const loadAdminCommerce=()=>readManagement('adminCommerce','/admin/commerce');
const loadAdminMerchantRefunds=()=>readManagement('adminMerchantRefunds','/admin/commerce/card-refunds',MerchantRefundOverviewSchema.parse);
function merchantRefundMutation(path:string,body:unknown){return operation('Update merchant refund',async()=>{
  const generation=authGeneration;
  try{MerchantRefundSchema.parse(await request(`/admin/commerce/card-refunds${path}`,{body,csrf:session().csrfToken}));}
  catch(error){if(generation===authGeneration)await loadAdminMerchantRefunds().catch(()=>undefined);throw error;}
  if(generation!==authGeneration)throw new Error('The account changed during the refund update.');
  await loadAdminMerchantRefunds();
},'admin');}
function cardRefundMutation(path:string,body:unknown){return operation('Update card refund',async()=>{
  const generation=authGeneration;let response:CardRefund;
  try{response=CardRefundSchema.parse(await request(path,{body,csrf:session().csrfToken}));}
  catch(error){if(generation===authGeneration)await loadAdminCardRefunds().catch(()=>undefined);throw error;}
  if(generation!==authGeneration)throw new Error('The account changed during the refund update.');
  await loadAdminCardRefunds();return response;
},'admin');}
function cryptoRefundMutation(path:string,body:unknown,admin:boolean){return operation('Update refund',async()=>{
  const generation=authGeneration;let response:CryptoRefund;
  try{response=CryptoRefundSchema.parse(await request(path,{body,csrf:session().csrfToken}));}
  catch(error){if(generation===authGeneration)await (admin?loadAdminCryptoRefunds():loadBillingRefunds()).catch(()=>undefined);throw error;}
  if(generation!==authGeneration)throw new Error('The account changed during the refund update.');
  await (admin?loadAdminCryptoRefunds():loadBillingRefunds());return response;
},admin?'admin':'billing');}
async function commerceMutation<T>(path:string,body:unknown,admin=false):Promise<T>{
  const generation=authGeneration;
  return operation('Save commerce changes',async()=>{
    const result=await request<T>(`${admin?'/admin/commerce':'/account/commerce'}${path}`,{body,csrf:session().csrfToken});
    if(generation!==authGeneration)throw new Error('The signed-in account changed during this operation.');
    await (admin?loadAdminCommerce():loadCommerce());
    return result;
  },admin?'admin':'commerce');
}
export const hermesActions = {
  loadAdminMerchantRefunds,
  respondMerchantDispute:(id:string,body:MerchantDisputeResponseInput)=>operation('Update dispute response',async()=>{
    const generation=authGeneration;
    let result;
    try{result=MerchantDisputeSchema.parse(await request(`/admin/commerce/card-disputes/${encodeURIComponent(id)}/response`,{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminMerchantRefunds().catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The signed-in account changed during this operation.');
    await loadAdminMerchantRefunds();return result;
  },'admin'),
  createMerchantRefund:(body:MerchantRefundRequest)=>merchantRefundMutation('',body),
  changeMerchantRefund:(id:string,body:MerchantRefundActionInput)=>merchantRefundMutation(`/${encodeURIComponent(id)}/action`,body),
  loadAdminPaymentReview,
  reviewCryptoNetwork:(body:NetworkReviewApproval)=>operation('Review payment network',async()=>{
    const generation=authGeneration;let result;
    try{result=walletNetworkReviewReceipt.parse(await request('/admin/billing/payment-review/network',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminPaymentReview(state.adminPaymentReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during network review.');
    await loadAdminPaymentReview(state.adminPaymentReview.data?.filter);return result;
  },'admin'),
  reviewCryptoPayment:(body:PaymentReviewInput)=>operation('Process crypto payment',async()=>{
    const generation=authGeneration;let result;
    try{result=PaymentReviewResult.parse(await request('/admin/billing/payment-review/action',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminPaymentReview(state.adminPaymentReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during the payment action.');
    await loadAdminPaymentReview(state.adminPaymentReview.data?.filter);return result;
  },'admin'),
  loadBillingCardRefunds,loadAdminCardRefunds,
  loadAdminCardReview,
  loadAdminUpgradeReview,
  loadAdminMembershipReview,
  closeObsoleteMembership:(body:MembershipReviewRequest)=>operation('Review subscription change',async()=>{
    const generation=authGeneration;let result;
    try{result=MembershipClosureResult.parse(await request('/admin/billing/membership-review/close',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminMembershipReview(state.adminMembershipReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)return result;
    await loadAdminMembershipReview(state.adminMembershipReview.data?.filter);return result;
  }),
  retryPaidUpgrade:(body:UpgradeReviewRequest)=>operation('Review paid upgrade',async()=>{
    const generation=authGeneration;let result;
    try{result=UpgradeReviewResult.parse(await request('/admin/billing/upgrade-review/retry',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminUpgradeReview(state.adminUpgradeReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during upgrade review.');
    await loadAdminUpgradeReview(state.adminUpgradeReview.data?.filter);return result;
  },'admin'),
  loadAdminRefundClosures,
  resolveRefundedUpgrade:(body:UpgradeReviewRequest)=>operation('Resolve refunded upgrade',async()=>{
    const generation=authGeneration;let result;
    try{result=UpgradeResolutionResult.parse(await request('/admin/billing/upgrade-review/resolve-refunded',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminUpgradeReview(state.adminUpgradeReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during upgrade review.');
    await Promise.all([loadAdminUpgradeReview(state.adminUpgradeReview.data?.filter),loadAdminRefundClosures(state.adminRefundClosures.data?.filter)]);return result;
  },'admin'),
  closeRefundedPayment:(body:RefundClosureRequest)=>operation('Close refunded payment review',async()=>{
    const generation=authGeneration;let result;
    try{result=RefundClosureResult.parse(await request('/admin/billing/refund-closures/close',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminRefundClosures(state.adminRefundClosures.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during payment review.');
    await loadAdminRefundClosures(state.adminRefundClosures.data?.filter);return result;
  },'admin'),
  reviewCardPayment:(body:CardReviewInput)=>operation('Review card request',async()=>{
    const generation=authGeneration;let result;
    try{result=CardReviewResult.parse(await request('/admin/billing/card-review/action',{body,csrf:session().csrfToken}));}
    catch(error){if(generation===authGeneration)await loadAdminCardReview(state.adminCardReview.data?.filter).catch(()=>undefined);throw error;}
    if(generation!==authGeneration)throw new Error('The account changed during card request review.');
    await loadAdminCardReview(state.adminCardReview.data?.filter);return result;
  },'admin'),
  createCardRefund:(body:CardRefundRequest)=>cardRefundMutation('/admin/billing/card-refunds',body),
  changeCardRefund:(id:string,body:CardRefundActionInput)=>cardRefundMutation(`/admin/billing/card-refunds/${encodeURIComponent(id)}/action`,body),
  loadBillingRefunds,loadAdminCryptoRefunds,
  confirmCryptoRefund:(id:string,destination:string)=>cryptoRefundMutation(`/account/billing/crypto-refunds/${encodeURIComponent(id)}/destination`,{destination,confirm:true},false),
  createCryptoRefund:(body:import('../../src/billing-refund-types').CryptoRefundCreateInput)=>cryptoRefundMutation('/admin/billing/crypto-refunds',body,true),
  changeCryptoRefund:(id:string,body:CryptoRefundActionInput)=>cryptoRefundMutation(`/admin/billing/crypto-refunds/${encodeURIComponent(id)}/action`,body,true),
   setDomainReminders:(id:string,enabled:boolean)=>operation('Save domain reminders',async()=>{
    const generation=authGeneration;
    await request(`/account/domains/${encodeURIComponent(id)}/reminders`,{body:{enabled},csrf:session().csrfToken});
    if(generation!==authGeneration)throw new Error('The signed-in account changed during this operation.');
    await loadDomains();
   },'commerce'),
   loadDomains,registerDomain,loadAdminDomains,renewDomain,reviewDomainRenewal,reviewDomainTransfer,transferDomain,cancelDomainTransfer,reviewIncomingTransfer,requestDomainAccountTransfer,decideDomainAccountTransfer,
  loadCommerce,loadCommerceEarnings,loadAdminCommerce,commerceMutation,
  loadMerchantCard,loadMerchantCardOptions,loadMerchantCrypto,loadMerchantCryptoOptions,
  createMerchantOffer:(body:MerchantCardOfferRequest|MerchantCryptoOfferRequest)=>merchantMutation(body.product,'/offers',body),
  changeMerchantOffer:(product:'payments'|'crypto',id:string,body:{requestId:string;state:'published'|'archived'})=>merchantMutation(product,`/offers/${encodeURIComponent(id)}/state`,body),
  createMerchantCheckout:(product:'payments'|'crypto',body:{requestId:string;offerId:string})=>merchantMutation(product,'/checkouts',body,MerchantCardCreatedSchema.parse),
  setMerchantCollection:(product:'payments'|'crypto',body:{requestId:string;enabled:boolean})=>merchantMutation(product,'/collection',body),

  setupAccount: (planId: string, requestId: string, signal: AbortSignal, retry = false) =>
    operation('Prepare your EightBots workspace', () => setupAccount(planId, requestId, signal, retry), 'setup'),
  refreshPreparation:async()=>{
    if(!state.session.data)return;
    await Promise.allSettled([refreshAccountSettings(),refreshHermesRuntime(),refreshNativeSessions()]);
  },
  reconcileReservation:(id:string,body:ReservationReviewInput)=>operation('Reconcile reserved credits',async()=>{await request(`/admin/reservations/${encodeURIComponent(id)}/reconcile`,{body,csrf:session().csrfToken});await loadAdminCatalog();},'admin'),
  previewCreditRecovery:(accountId:string)=>operation('Review credit recovery',()=>request<CreditRecoveryPreview>(`/admin/accounts/${encodeURIComponent(accountId)}/credit-recovery`),'admin'),
  restoreCreditAvailability:(accountId:string,body:CreditRecoveryInput)=>operation('Recover existing credits',async()=>{
    const result=await adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/credit-recovery`,'POST',body,accountId);
    if(accountId===state.session.data?.accountId)await refreshAccountBalance();
    return result;
  },'admin'),
  saveModelPolicy:(body:AdminCatalog['policy']&{reason:string})=>operation('Save model defaults',async()=>{await request('/admin/model-policy',{method:'PUT',body,csrf:session().csrfToken});await loadAdminCatalog();},'admin'),
  connectAdminMailSender:(reason:string)=>operation('Connect sign-in email',async()=>{
    if(platform().kind==='desktop') {
      await platform().openAccountPage(`${platform().publicOrigin}/admin/mail-sender`);
      return;
    }
    const generation=authGeneration;
    const result=await request<{authorizationUrl:string}>('/admin/mail-sender/connect',{body:{reason},csrf:session().csrfToken});
    if(generation!==authGeneration)throw new Error('The account changed before the email connection opened.');
    const target=new URL(result.authorizationUrl);
    if(target.origin!=='https://accounts.google.com'||target.username||target.password)throw new Error('The email service returned an invalid authorization address.');
    window.location.assign(target.href);
  },'admin'),
  disconnectAdminMailSender:(reason:string)=>operation('Disconnect sign-in email',async()=>{
    const generation=authGeneration;
    await request('/admin/mail-sender',{method:'DELETE',body:{reason},csrf:session().csrfToken});
    if(generation===authGeneration)await loadAdminMailSender();
  },'admin'),
  removeSharedProvider:(provider:'mimo'|'openai',body:{reason:string})=>operation('Remove shared connection',async()=>{await request(`/admin/providers/${provider}`,{method:'DELETE',body,csrf:session().csrfToken});await loadAdminCatalog();},'admin'),
  adminModelKey:(accountId:string,method:'PUT'|'DELETE',body:{provider:'mimo'|'openai';reason:string;apiKey?:string})=>operation('Update model connection',()=>adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/model-key`,method,body,accountId),'admin'),
  saveSharedProvider:(provider:'mimo'|'openai',body:{apiKey:string;reason:string})=>operation('Save shared connection',async()=>{await request(`/admin/providers/${provider}`,{method:'PUT',body,csrf:session().csrfToken});await loadAdminCatalog();},'admin'),
  resetWeeklyLimit:(requestId:string)=>operation('Reset weekly allowance',async()=>{
    await request('/account/reset-weekly-limit',{body:{requestId},csrf:session().csrfToken});
    await Promise.all([refreshAccountBalance(),loadMessages(null,'category=allowance')]);
  },'weekly-reset'),
  prepareAccount:()=>operation('Prepare your team',async()=>{
    await request('/account/prepare',{body:{},csrf:session().csrfToken});await refreshAccount();
  }),
  adminResetWeeklyLimit:(accountId:string,body:{requestId:string;reason:string})=>operation('Reset weekly allowance',
    ()=>adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/reset-weekly-limit`,'POST',body,accountId),'admin'),
  loadAdminAccess, loadAdminOverview, loadAdminVirtualMachines, loadAdminAccounts, loadAdminAccount, loadAdminCredits, loadAdminUsage,
  loadAdminPromotions,
  adminIssuePromotion:(accountId:string,body:PromotionCreate)=>operation('Save promotional code',()=>promotionMutation(accountId,body),'admin'),
  adminRevokePromotion:(accountId:string,id:string,reason:string)=>operation('Disable promotional code',()=>promotionMutation(accountId,{reason},id),'admin'),
  redeemPromotion:(code:string)=>operation('Activate promotional subscription',async()=>{
    const generation=authGeneration;
    const result=PromotionRedemptionSchema.parse(await request('/account/billing/promotions/redeem',{body:{code},csrf:session().csrfToken}));
    if(generation!==authGeneration)throw new Error('The account changed while the code was applied.');
    managementVersions.set('accountBilling',(managementVersions.get('accountBilling')??0)+1);
    publish({accountBilling:ready(result.billing)});await refreshSession();
    if(generation!==authGeneration)throw new Error('The account changed while the code was applied.');
    await loadBillingPlans();
    if(generation!==authGeneration)throw new Error('The account changed while the code was applied.');
    return result;
  },'billing'),
  loadInvoices, loadAdminCatalog, loadAdminMailSender, loadAdminHealth, loadAdminAudit, loadAdminBilling, loadBillingPlans, loadPublicPaymentMethods, loadAccountBilling,
  loadMessages,loadMessageInbox,
  sendAccountMessage:(accountId:string|null,body:{requestId:string;body:string})=>operation('Send message',async()=>{
    const generation=authGeneration;
    await request(accountId?`/admin/messages/${encodeURIComponent(accountId)}`:'/account/messages',{body,csrf:session().csrfToken});
    if(generation===authGeneration)await loadMessages(accountId);
  },accountId?'admin':'account'),
  readAccountMessages:(accountId:string|null,throughId:string,search:string)=>operation('Mark messages as read',async()=>{
    const generation=authGeneration;
    await request(accountId?`/admin/messages/${encodeURIComponent(accountId)}/read`:'/account/messages/read',{body:{throughId},csrf:session().csrfToken});
    if(generation===authGeneration)await loadMessages(accountId,search);
  },accountId?'admin':'account'),
  loadBillingPaymentOptions,loadBillingPayments,loadBillingPayment,quoteBillingCredits,
  beginBillingPurchase:(body:BillingPurchaseRequest)=>operation('Create payment',()=>billingPaymentMutation('/account/billing/payments',body),'billing'),
  prepareBillingCard:(id:string,expectedPaidCents:number)=>operation('Prepare card payment',()=>billingPaymentMutation(`/account/billing/payments/${encodeURIComponent(id)}/card`,{expectedPaidCents}),'billing'),
  changeMembership:(body:MembershipAction)=>billingAccountMutation('/account/billing/membership',body),
  changeRenewalMethod:(body:BillingMethodRequest)=>billingAccountMutation('/account/billing/renewal-method',body),
  cancelRenewalMethod:(id:string)=>billingAccountMutation(`/account/billing/renewal-method/${encodeURIComponent(id)}/cancel`,{}),
  reconcileRenewalMethod:(subscriptionId:string)=>billingAccountMutation('/account/billing/renewal-method/reconcile',{subscriptionId}),
  confirmBillingPayment:(confirm:()=>Promise<void>)=>operation('Confirm payment',confirm,'billing'),
  adminSetAccountAccess: (accountId: string, body: AdminAccountAccessRequest) => operation('Save account access',
    () => adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/access`, 'PUT', body, accountId), 'admin'),
  adminSetAccountRole: (accountId: string, body: AdminAccountRoleRequest) => operation('Save administrative role',
    () => adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/role`, 'PUT', body, accountId), 'admin'),
  adminRevokeSessions: (accountId: string, body: {reason: string}) => operation('Revoke sessions',
    () => adminMutation(`/admin/accounts/${encodeURIComponent(accountId)}/revoke-sessions`, 'POST', body, accountId), 'admin'),
  adminGrantCredit: (accountId: string, body: AdminCreditGrantRequest) => operation('Record credit grant',
    async () => {
      const result=await adminMutation<AdminGrantResult>(`/admin/accounts/${encodeURIComponent(accountId)}/credit-grants`, 'POST', body, accountId);
      if(accountId===state.session.data?.accountId)await Promise.allSettled([refreshAccountBalance()]);
      return result;
    }, 'admin'),
  adminCreateBillingPlan: (body: {plan: BillingPlanDraft; reason: string; requestId: string}) => operation('Create subscription plan',
    () => adminMutation<BillingPlan>('/admin/billing/plans', 'POST', body), 'admin'),
  adminPublishBillingPlan: (id: string, body: {reason: string}) => operation('Publish subscription plan',
    () => adminMutation<BillingPlan>(`/admin/billing/plans/${encodeURIComponent(id)}/publish`, 'POST', body), 'admin'),
  adminArchiveBillingPlan: (id: string, body: {reason: string}) => operation('Archive subscription plan',
    () => adminMutation<BillingPlan>(`/admin/billing/plans/${encodeURIComponent(id)}/archive`, 'POST', body), 'admin'),
  activateBillingPlan: (planId: string, requestId: string) => operation('Activate selected plan', async () => {
    const snapshot = await activateBillingPlan(planId, requestId);
    await refreshAccountBalance();
    return snapshot;
  }, 'billing'),
  upgradeBillingPlan: (planId:string,expectedPlanId:string,requestId:string) => operation('Upgrade selected plan',async()=>{
    const generation=authGeneration;
    const result=await request<{billing:unknown;balance:unknown}>('/account/billing/upgrade',{body:{planId,expectedPlanId,requestId},csrf:session().csrfToken});
    const billing=AccountBillingSchema.parse(result.billing),balance=AccountCreditBalanceSchema.parse(result.balance);
    if(generation!==authGeneration)throw new Error('The account changed before the upgrade completed.');
    managementVersions.set('accountBilling',(managementVersions.get('accountBilling')??0)+1);
    accountBalanceGeneration++;
    publish({accountBilling:ready(billing),accountBalance:ready(balance)});
    return billing;
  },'billing'),
  openBillingPortal: () => operation('Open billing management', () => billingRedirect('/account/billing/portal', {}), 'billing'),
  confirmBillingReturn: () => operation('Confirm billing status', async () => {
    const generation = authGeneration;
    const snapshot = AccountBillingSchema.parse(await request('/account/billing/sync', {body: {}, csrf: session().csrfToken}));
    if (generation !== authGeneration) throw new Error('The account changed before billing confirmation completed.');
    managementVersions.set('accountBilling', (managementVersions.get('accountBilling') ?? 0) + 1);
    publish({accountBilling: ready(snapshot)});
    await refreshSession();
    void refreshAccountBalance().catch(() => undefined);
    return snapshot;
  }, 'billing'),
  load(): Promise<void> {
    if (initialLoad) return initialLoad;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const returnedGoogleError = fragment.get(GOOGLE_ERROR_FRAGMENT);
    if (returnedGoogleError !== null) {
      fragment.delete(GOOGLE_ERROR_FRAGMENT);
      const remaining = fragment.toString();
      window.history.replaceState(window.history.state, '',
        `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`);
      try {
        const {stack, errors, ...details} = GoogleCallbackErrorSchema.parse(JSON.parse(returnedGoogleError));
        const cause = errors === undefined ? details.cause : {cause: details.cause, errors};
        publish({operation: {label: null, scope: 'login', error: {...details,
          ...(stack === undefined ? {} : {stack}), ...(cause === undefined ? {} : {cause})}}});
      }
      catch (error) {publish({operation: {label: null, scope: 'login', error: errorDetails(error)}});}
    }
    initialLoad = (async () => {
      const returnParameters=new URLSearchParams(window.location.search);
      const errorProvider=(['google','discord'] as const).find(provider=>returnParameters.get(provider)==='error');
      if(errorProvider){
        const scope=window.location.pathname.endsWith('/admin/mail-sender')?'admin':
          window.location.pathname.endsWith('/settings')?'account':'login';
        returnParameters.delete(errorProvider);
        window.history.replaceState(window.history.state,'',`${window.location.pathname}${returnParameters.size?`?${returnParameters}`:''}${window.location.hash}`);
        try{
          const {stack,errors,...details}=GoogleCallbackErrorSchema.parse(await request(`/auth/${errorProvider}/error`));
          const cause=errors===undefined?details.cause:{cause:details.cause,errors};
          publish({operation:{label:null,scope,error:{...details,...(stack===undefined?{}:{stack}),...(cause===undefined?{}:{cause})}}});
        }catch(error){publish({operation:{label:null,scope,error:errorDetails(error)}});}
      }
      window.addEventListener('focus',refreshAccountOnFocus);
      window.addEventListener(UNSAVED_WORK_EVENT, event => {
        if (state.operation.label !== null) (event as UnsavedWorkQuery).detail.blocked = true;
      });
      window.addEventListener('desktop-authenticated', () => {
        // A read started before native sign-in must not replace the new session.
        sessionLoadGeneration++;
        void hermesActions.refresh().catch(report => {
          publish({operation: {label: null, scope: 'login', error: errorDetails(report)}});
        });
      });
      window.addEventListener('eightbots-platform-error', event => publish({operation: {
        label: null, scope: 'account', error: errorDetails((event as CustomEvent<unknown>).detail),
      }}));
      window.addEventListener('popstate',()=>nativeInputOrigins.clear());
      document.addEventListener('visibilitychange',refreshAccountOnFocus);
      document.addEventListener('visibilitychange',resumeWorkspaceConnection);
      document.addEventListener('resume',resumeWorkspaceConnection);
      document.addEventListener('freeze',resumeWorkspaceConnection);
      window.addEventListener('pagehide',resumeWorkspaceConnection);
      window.addEventListener('pageshow',resumeWorkspaceConnection);
      window.addEventListener('pageshow',event=>{if(event.persisted)refreshAccountOnFocus();});
      window.addEventListener('online',resumeWorkspaceConnection);
      window.addEventListener('focus',resumeWorkspaceConnection);
      await Promise.allSettled([query('config', loadConfig), query('health', () => request<Health>('/health', {health: true})),
        refreshSession().then(() => {if (state.session.data) void refreshAccount();})]);
    })();
    return initialLoad;
  },
  async refresh(): Promise<void> {
    await Promise.allSettled([query('config', loadConfig), query('health', () => request<Health>('/health', {health: true})), refreshSession()]);
    if (state.session.data) await refreshAccount();
  },
  connectWallet: () => operation('Connect wallet', async () => {
    const wallet = await import('./wallet');
    const address = await wallet.connectWallet(config()); publish({walletAddress: address}); return address;
  }),
  addWalletNetwork: () => operation('Add wallet network', async () => (await import('./wallet')).addWalletNetwork(config())),
  signIn: (email: string, password: string) => operation('Signing in…', () => authenticateWithPassword('/auth/login', email, password), 'login'),
  pendingEmailCode: () => platform().pendingEmailCode(),
  startEmailCode: (email: string) => operation('Sending your sign-in code…', async () => {
    if (currentSession()) throw new Error('Sign out before connecting another account.');
    return platform().startEmailCode(EmailCodeStartSchema.parse({email}).email);
  }, 'login'),
  finishEmailCode: (code: string) => operation('Signing in…', async () => {
    if (currentSession()) throw new Error('Sign out before connecting another account.');
    await platform().finishEmailCode(EmailCodeFinishSchema.shape.code.parse(code));
    return readDesktopSignIn();
  }, 'login'),
  cancelEmailCode: () => operation('Change sign-in email', () => platform().cancelEmailCode(), 'login'),
  register: (email: string, password: string) => operation('Creating your account…', () => authenticateWithPassword('/auth/register', email, password), 'register'),
  setupLogin: (token: string, email: string, password: string) => operation('Saving sign-in details…', () => authenticateWithPassword('/auth/setup-login', email, password, token), 'setup-login'),
  signInWithWallet: () => operation('Complete sign-in in your wallet.', async () => {
    const wallet = await import('./wallet');
    const configured = config();
    const address = await wallet.connectWallet(configured); publish({walletAddress: address});
    stopWalletWatch?.(); stopWalletWatch = wallet.watchWallet(accounts => publish({walletAddress: accounts[0] ?? null}));
    const challenge = await request<{challengeId: string; message: string; expiresAt: string}>('/auth/challenge', {body: {address}});
    const signature = await wallet.signWalletMessage(configured, address, challenge.message);
    const authenticated = await request<Session>('/auth/verify', {body: {challengeId: challenge.challengeId, signature}});
    acceptSession(authenticated); await refreshAccount(); return authenticated;
  }, 'login'),
  signOut: () => operation('Sign out', async () => {
    const authenticated = session();
    try {await request<void>('/auth/logout', {body: {}, csrf: authenticated.csrfToken});}
    catch (error) {
      // An expired session is already revoked, but explicit sign-out still clears local drafts.
      if (!(error instanceof ApiError) || error.details.status !== 401 || error.details.code === 'desktop_account_page_session_ended') throw error;
    }
    await platform().logout();
    clearChatDrafts(authenticated.accountId);
    acceptSession(null);
    clearWorkspaceContext();
  },'logout'),
  openHermes, closeHermes, selectNativeAgent, refreshHermesRuntime, reconnectNativeTokens, revalidateSession,
  setNativeWorkDetailsOpen: (sessionId: string | null, open: boolean): void => {
    if (sessionId === null || state.nativeTarget?.sessionId !== sessionId || state.nativeWorkDetailsOpen === open) return;
    publish({nativeWorkDetailsOpen: open});
    if (state.nativeConnection.status === 'open') {
      // The existing detail resource owns loading, errors and all activity data.
      void refreshNativeDetail(sessionId, nativeGeneration, true).catch(() => undefined);
    }
  },
  refreshNativeSessions,
  loadOlderNativeMessages,
  createNativeChat: (title?: string) => operation('Create work chat', async () => {
    const generation = authGeneration;
    const created = await request<NativeSessionMapping>('/hermes/sessions',
      {body: {mode: 'create', ...(title === undefined ? {} : {title})}, csrf: session().csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before the work chat was created.');
    // A failed list read must not turn a completed create into a second create.
    void refreshNativeSessions().catch(() => undefined);
    return created;
  }),
  openNativeTerminal: async (sessionId: string, agentId: string | null, attachId: string, signal: AbortSignal): Promise<NativeTerminalTicket> => {
    const authenticated = session();
    const generation = authGeneration;
    const ticket = await request<NativeTerminalTicket>(`/hermes/sessions/${encodeURIComponent(sessionId)}/terminal`,
      {body: {...(agentId === null ? {} : {agentId}), attachId}, csrf: authenticated.csrfToken, signal});
    if (generation !== authGeneration) throw new Error('The account changed before the terminal opened.');
    return ticket;
  },
  refreshHermes: async (sessionId: string | null = state.nativeTarget?.sessionId ?? null) => {
    session();
    // This is the explicit Reconnect action, including a failed work reader
    // while communication is still healthy. Replace the stopped transports.
    return openHermes(sessionId, true);
  },
  configureHermes: () => operation(PRODUCT_ENTRY_LABEL, async () => {
    const generation = authGeneration;
    const runtime = await request<NativeRuntimeStatus>('/hermes/runtime', {body: {}, csrf: session().csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before Hermes started.');
    nativeRuntimeGeneration++;
    publish({nativeRuntime: ready(runtime)});
    await Promise.allSettled([refreshAccountSettings(true)]);
    return runtime;
  }),
  sendNativeMessage: async (text: string, attachmentIds: string[] = [], expectedTarget?: NativeChatTarget, requestId?: string, recover = false) => {
    return sendNativeMessage(text, attachmentIds, expectedTarget, requestId, recover);
  },
  uploadChatAttachment: (file: File, expectedTarget?: NativeChatTarget) => uploadChatAttachment(file, expectedTarget),
  answerNativeRequest: (requestId: string, result: Record<string, unknown>) => operation('Send answer',
    () => respondToNativeRequest(requestId, {result})),
  cancelNativeRequest: (requestId: string, message: string) => operation('Close request',
    () => respondToNativeRequest(requestId, {cancel: true, message})),
  answerNativeConnection: (ownerSessionId: string, opId: string, result: Record<string, unknown>) => operation('Respond to connection', async () => {
    const detail = state.nativeDetail.data, authenticated = session();
    if (!detail || state.nativeConnection.status !== 'open' || state.accountSettings.error !== null
      || state.nativeTarget?.sessionId !== detail.session.sessionId) throw new Error('Connect to your work chat before responding.');
    if (!detail.pendingConnections.some(item => item.sessionId === ownerSessionId && item.operation.op_id === opId)) {
      throw new Error('This connection request is no longer open.');
    }
    const generation = nativeGeneration, accountGeneration = authGeneration;
    try {
      const response = await request<{status: string; settled: boolean}>(`/hermes/sessions/${encodeURIComponent(detail.session.sessionId)}/connections/${encodeURIComponent(opId)}`,
        {body: {ownerSessionId, result}, csrf: authenticated.csrfToken});
      if (response.status !== 'ok') throw new Error('Hermes did not confirm this connection response.');
      return response;
    } finally {
      if (generation === nativeGeneration && accountGeneration === authGeneration) refreshAfterNativeAction(detail.session.sessionId, generation);
    }
  }),
  interruptNativeAgent: (expectedTarget?: NativeChatTarget) => operation('Stop agent', async () => {
    const {sessionId, agentId} = requireNativeChatTarget(expectedTarget);
    const generation = nativeGeneration;
    const path = `/hermes/sessions/${encodeURIComponent(sessionId)}` + (agentId === null ? '' : `/agents/${encodeURIComponent(agentId)}`);
    const result = await request<NativeMessageResult>(`${path}/interrupt`,
      {body: {}, csrf: session().csrfToken});
    if (generation === nativeGeneration) {publish({nativeMessageResult: ready(result)}); refreshAfterNativeAction(sessionId, generation);}
    return result;
  }),
  refreshAccountSettings, refreshAccountProfile, refreshAccountVirtualMachine, refreshAccountBalance, refreshStorage: refreshHermesRuntime,
  recoverAccountVirtualMachine: () => operation('Recover VM', recoverAccountVirtualMachine),
  refreshAccountDataReset,
  clearAccountData: (confirmation: string) => operation('Clear all user data', () => clearAccountData(confirmation)),
  refreshApplications, readApplicationLogs,
  registerApplication: (definition: ApplicationRegistration) => operation('Add service', () => changeApplication('/account/applications', definition)),
  startApplication: (slug: string) => operation('Start service', () => changeApplication(`/account/applications/${encodeURIComponent(slug)}/start`, {})),
  stopApplication: (slug: string) => operation('Stop service', () => changeApplication(`/account/applications/${encodeURIComponent(slug)}/stop`, {})),
  closeApplicationLogs(): void {
    applicationLogsGeneration++;
    publish({applicationLogSlug: null, applicationLogs: idle<ApplicationLogs>()});
  },
  saveAccountUsername: (username: string, expectedUsername: string) => operation('Save username', () => changeAccountUsername(username, expectedUsername)),
  refreshWhatsApp,
  refreshGoogle,
  connectGoogle: (services: GoogleService[]) => operation('Connect Google', () => authorizeGoogle('/account/google/connect', {services})),
  linkGoogleSignIn: () => operation('Enable Google sign-in', () => authorizeGoogle('/account/google/link-sign-in', {})),
  signInGoogle: (returnPath: string, mode: 'register' | 'login', planId?: string) => operation('Continue with Google', () => authorizeGoogle('/auth/google/start', {returnPath, mode, ...(planId ? {planId} : {})}), mode),
  refreshDiscord,
  linkDiscordSignIn: () => operation('Enable Discord sign-in', () => authorizeIdentity('discord', '/account/discord/link-sign-in', {})),
  signInDiscord: (returnPath: string, mode: 'register' | 'login') => operation('Continue with Discord', () => authorizeIdentity('discord', '/auth/discord/start', {returnPath, mode}), mode),
  disconnectGoogle: () => operation('Disconnect Google', async () => {
    const generation = authGeneration;
    const status = GoogleStatusSchema.parse(await request('/account/google', {method: 'DELETE', body: {}, csrf: session().csrfToken}));
    if (generation !== authGeneration) throw new Error('The account changed before Google was disconnected.');
    googleGeneration++;
    publish({google: ready(status)});
    return status;
  }),
  pairWhatsApp: (sessionId?: string) => operation('Connect WhatsApp', async () => {
    const generation = authGeneration;
    const status = await changeWhatsApp('/account/whatsapp/pair', 'POST', sessionId === undefined ? {} : {sessionId});
    await refreshStartedWorkspace(generation);
    if (generation !== authGeneration) throw new Error('The account changed before the workspace state was refreshed.');
    return status;
  }),
  disconnectWhatsApp: () => operation('Disconnect WhatsApp', () => changeWhatsApp('/account/whatsapp', 'DELETE')),
  saveMessagePrefix: (enabled: boolean) => operation('Save message prefix', () =>
    changeAccountSettings('/account/message-prefix', 'PUT', {enabled})),
  saveAccountDocument: (name: string, content: string, expectedRevision: AccountDocument['revision'] | null) => operation('Save document', async () => {
    const settings = await changeAccountSettings(`/account/documents/${encodeURIComponent(name)}`, 'PUT', {content, expectedRevision});
    const document = settings.documents?.find(item => item.name === name);
    if (!document) throw new Error('The service did not return the saved document.');
    await Promise.allSettled([refreshHermesRuntime()]);
    return document;
  }),
  deleteAccountDocument: (name: string, expectedRevision: AccountDocument['revision']) => operation('Delete document', () =>
    changeAccountSettings(`/account/documents/${encodeURIComponent(name)}`, 'DELETE', {expectedRevision})),
  clearError(): void {publish({operation: {...state.operation, error: null}});}
};

export function useHermes() {
  const snapshot = useSyncExternalStore(callback => {listeners.add(callback); return () => listeners.delete(callback);}, () => state);
  return {state: snapshot, actions: hermesActions};
}
import type {CreditRecoveryInput,CreditRecoveryPreview} from '../../src/credit-recovery';
